import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { AvvisoArretrati } from "../components/PagamentiArretrati";
import { PagamentiSection, canoneServizi } from "../components/PagamentiCliente";
import {
  ArrowLeft,
  Users,
  User,
  Target,
  FileText,
  TrendingUp,
  Plus,
  Trash2,
  MessageSquare,
  BarChart3,
  Pencil,
  Search,
  Package,
  Mail,
  Phone,
  MapPin,
  AlertTriangle,
  CheckCircle2,
  Check,
  Reply,
  ChevronDown,
  ChevronUp,
  X,
  FileSignature,
  CalendarClock,
  RefreshCw,
  Repeat,
  Zap,
  Percent
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { AMBITI_CONTRATTO, ambitoDi, percorsoContratto } from "../utils/ambitiContratto";
import AlertRichiamoDialog from "../components/AlertRichiamoDialog";
import { SECONDARY_BTN, PRIMARY_BTN, PRIMARY_BTN_SM, GHOST_BTN, FIELD_CLS, DIALOG_CONTENT_CLS, ICON_TILES, SectionCard, UserAvatar, StatBlock, StatusPill, Chip, MonthPicker, DatePicker, EmptyState, InfoTooltip, InfoHint, NumberField, SegmentedControl, BarraScadenza, aperturaRinnovo, rinnovoAperto, Badge as BadgeTono } from "../components/designSystem";

export default function SchedaCliente({ user }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scheda, setScheda] = useState(null);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(!clientId);
  const [tiktokInfo, setTiktokInfo] = useState(null);
  const [tiktokRevenue, setTiktokRevenue] = useState("");
  const [tiktokSaving, setTiktokSaving] = useState(false);
  const [benchmarks, setBenchmarks] = useState({ cpr: "", cac: "" });
  const [savingBench, setSavingBench] = useState(false);
  const [benchSaved, setBenchSaved] = useState(false);
  const [teamOpenSignal, setTeamOpenSignal] = useState(0);
  // Cresce quando lo stepper dei Pagamenti cambia la percentuale del mese: la
  // sezione Fatturato percentuale ha un suo registro e va riletta.
  const [versionePct, setVersionePct] = useState(0);
  const teamCardRef = useRef(null);

  useEffect(() => {
    if (clientId) {
      fetchScheda(clientId);
      fetchTiktokInfo(clientId);
      setShowSearch(false);
    } else {
      fetchClients();
      setLoading(false);
    }
  }, [clientId]);

  const fetchTiktokInfo = async (id) => {
    const res = await apiFetch(`/clients/${id}/tiktok-info`);
    if (res.ok) {
      const d = await res.json();
      setTiktokInfo(d);
      setTiktokRevenue(String(d.tiktok_revenue_generated || 0));
    }
  };

  const saveTiktokRevenue = async () => {
    if (!clientId) return;
    setTiktokSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/tiktok-info`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tiktok_enabled: true, tiktok_revenue_generated: Number(tiktokRevenue || 0) }),
      });
      if (res.ok) {
        setTiktokInfo(await res.json());
        toast.success("Fatturato generato aggiornato");
      } else toast.error("Errore salvataggio");
    } finally { setTiktokSaving(false); }
  };

  const fetchClients = async () => {
    const res = await apiFetch("/clients");
    if (res.ok) {
      const data = await res.json();
      setClients(data);
    }
  };

  const fetchScheda = async (id, { silent = false } = {}) => {
    // "silent" evita lo skeleton a pagina intera quando è un refresh dopo un'azione
    // (aggiungi fatturato, registra pagamento, ecc.): le sezioni restano montate e
    // si aggiornano in-place, così le loro animazioni di ingresso (es. la barra del
    // fatturato) restano visibili invece di sparire dietro al lampeggio della pagina.
    if (!silent) setLoading(true);
    try {
      const res = await apiFetch(`/clients/${id}/scheda`);
      if (res.ok) {
        const data = await res.json();
        setScheda(data);
        setBenchmarks({
          cpr: data?.client?.benchmark_cpr_max ?? "",
          cac: data?.client?.benchmark_cac_max ?? "",
        });
      } else {
        toast.error("Errore nel caricamento scheda");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const saveBenchmarks = async () => {
    if (!clientId) return;
    setSavingBench(true);
    try {
      const payload = {
        benchmark_cpr_max: benchmarks.cpr === "" ? null : Number(benchmarks.cpr),
        benchmark_cac_max: benchmarks.cac === "" ? null : Number(benchmarks.cac),
      };
      const r = await apiFetch(`/clients/${clientId}/benchmarks`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) { toast.success("Benchmark salvati"); setBenchSaved(true); }
      else { const err = await r.json().catch(() => ({})); toast.error(err.detail || "Errore"); }
    } finally { setSavingBench(false); }
  };

  const filteredClients = clients.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (showSearch || !clientId) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="scheda-search">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Scheda Cliente</h1>
          <p className="text-muted-foreground text-sm mt-1">Cerca un cliente per visualizzare la sua scheda</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Cerca cliente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              autoFocus
              data-testid="client-search-input"
            />
          </div>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {filteredClients.map(client => (
              <button
                key={client.client_id}
                onClick={() => navigate(`/scheda-cliente/${client.client_id}`)}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted/50 transition-colors flex items-center justify-between group"
                data-testid={`client-item-${client.client_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-indigo-600">{client.name?.charAt(0)}</span>
                  </div>
                  <span className="font-medium">{client.name}</span>
                </div>
                <Badge className={client.is_active !== false ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>
                  {client.is_active !== false ? "Attivo" : "Inattivo"}
                </Badge>
              </button>
            ))}
            {filteredClients.length === 0 && (
              <p className="text-center text-muted-foreground py-8">Nessun cliente trovato</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-muted rounded w-48"></div>
        <div className="h-40 bg-muted rounded-xl"></div>
        <div className="h-60 bg-muted rounded-xl"></div>
      </div>
    );
  }

  if (!scheda) return null;

  const client = scheda.client;
  const canone = canoneServizi(client);
  const meseCorrente = scheda.stato_pagamenti?.mese_corrente;
  const nomeMese = meseCorrente?.mese ? new Date(meseCorrente.mese + "-01").toLocaleDateString("it-IT", { month: "long" }) : "";
  const contrattoNovita = new Date() <= NOVITA_CONTRATTO_FINO_AL;
  const assignedCount = [scheda.team.project, scheda.team.social_media, scheda.team.content_creator, scheda.team.editor].filter(Boolean).length;

  return (
    <div className="space-y-[18px] animate-fade-in scheda-font-ui" data-testid="scheda-cliente">
      {/* Testata */}
      <div className="flex items-start gap-5 flex-wrap">
        <button
          onClick={() => navigate("/clients")}
          className="mt-1 h-9 px-3.5 rounded-[10px] border border-[#e0e0ea] bg-white text-[13px] font-semibold text-[#565d6d] hover:border-[#c9c9f0] hover:text-[#5b5bd6] transition-colors inline-flex items-center gap-1.5 shrink-0"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.9} />
          Indietro
        </button>

        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-[30px] font-extrabold leading-[1.15] tracking-[-0.02em] text-[#15161c]">{client.name}</h1>
            <StatusPill active={client.is_active !== false} />
            {/* Canone dai servizi attaccati, non dal vecchio importo_mensile. */}
            {canone.haMensili && (canone.fisso > 0 || canone.percentuale > 0) && (
              <Chip mono>
                {[
                  canone.fisso > 0 ? formatCurrency(canone.fisso, { decimals: 0 }) : null,
                  canone.percentuale > 0 ? `${formatNumber(canone.percentuale)}%` : null,
                ].filter(Boolean).join(" + ")}/mese
              </Chip>
            )}
            {/* Mese con attivazioni o una tantum: il dovuto supera il canone e
                va detto, come nella colonna € mensile della lista. */}
            {meseCorrente?.una_tantum > 0 && (
              <InfoTooltip
                title="Dovuto del mese"
                description={`${formatCurrency(meseCorrente.dovuto, { decimals: 0 })} dovuti a ${nomeMese}, di cui ${formatCurrency(meseCorrente.una_tantum, { decimals: 0 })} fra attivazioni e una tantum.`}
              >
                <Chip mono>{formatCurrency(meseCorrente.dovuto, { decimals: 0 })} a {nomeMese}</Chip>
              </InfoTooltip>
            )}
            {tiktokInfo?.has_ts_tiktok_shop_service && tiktokInfo.tiktok_agency_fee > 0 && (
              <span
                className="h-7 px-2.5 rounded-[8px] bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-700 text-[13px] font-medium inline-flex items-center scheda-font-mono"
                title={`Canone ${formatCurrency(tiktokInfo.canone_mensile, { suffix: true })} + ${formatNumber(tiktokInfo.tiktok_agency_pct)}% agenzia`}
              >
                Totale con {tiktokInfo.servizio_nome ? "quota" : "TikTok"}: {formatCurrency(tiktokInfo.monthly_total_agency, { decimals: 0, suffix: true })}
              </span>
            )}
          </div>
          {(client.cf_piva || client.referente || client.email || client.recapito_telefonico || client.sede_comune || client.contratto_id || client.contratto_url) && (
            <div className="flex flex-wrap items-center gap-2 mt-2.5" data-testid="cliente-anagrafica">
              {client.cf_piva && (
                <span className="h-7 px-2.5 rounded-[8px] bg-white border border-[#e7e7ef] text-[13px] font-medium text-[#3c414d] inline-flex items-center gap-1.5">
                  <span className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[#9095a1]">P.IVA</span>
                  <span className="scheda-font-mono">{client.cf_piva}</span>
                </span>
              )}
              {client.referente && <Chip icon={User}>{client.referente}</Chip>}
              {client.email && <Chip icon={Mail} href={`mailto:${client.email}`}>{client.email}</Chip>}
              {client.recapito_telefonico && <Chip icon={Phone} mono>{client.recapito_telefonico}</Chip>}
              {client.sede_comune && (
                <Chip icon={MapPin}>{[client.sede_via, client.sede_comune, client.sede_prov].filter(Boolean).join(", ")}</Chip>
              )}
              {/* Clienti nati dal wizard: si apre la scheda del contratto in /contratti.
                  Clienti precedenti: resta il link libero (Drive) impostato in Clienti. */}
              {client.contratto_id ? (
                <Chip
                  icon={FileSignature}
                  accent={contrattoNovita}
                  badge={contrattoNovita ? "News" : null}
                  onClick={() => navigate(`/contratti?contratto=${client.contratto_id}`)}
                  testId="cliente-link-contratto"
                >
                  Contratto
                </Chip>
              ) : client.contratto_url ? (
                <Chip
                  icon={FileSignature}
                  href={client.contratto_url}
                  external
                  accent={contrattoNovita}
                  badge={contrattoNovita ? "News" : null}
                  testId="cliente-link-contratto"
                >
                  Contratto
                </Chip>
              ) : null}
            </div>
          )}
        </div>

        <div className="h-10 px-3.5 rounded-[12px] border border-[#e7e7ef] bg-white flex items-center gap-2.5 shrink-0">
          <span className="text-[10px] font-bold uppercase tracking-wide text-[#9095a1]">Team</span>
          <span className={`text-[13px] font-medium ${assignedCount > 0 ? "text-[#15161c]" : "text-[#9095a1]"}`}>
            {assignedCount > 0 ? `${assignedCount} person${assignedCount === 1 ? "a" : "e"}` : "Nessun team assegnato"}
          </span>
          <button
            className="h-7 px-2.5 rounded-[8px] bg-[#f1f1f8] hover:bg-[#e7e7fb] text-[#5b5bd6] text-[12.5px] font-semibold transition-colors disabled:text-[#9095a1] disabled:hover:bg-[#f1f1f8] disabled:cursor-not-allowed"
            onClick={() => setTeamOpenSignal(s => s + 1)}
            disabled={teamInAttesaAttivazione(client)}
            title={teamInAttesaAttivazione(client) ? MSG_TEAM_BLOCCATO : undefined}
          >
            {assignedCount > 0 ? "Gestisci" : "Assegna"}
          </button>
        </div>
      </div>

      {/* Fatturato generato al cliente, valore unico: solo per i clienti col
          vecchio tag ts_tiktok_shop. Con un servizio del Catalogo a percentuale
          il fatturato si inserisce mese per mese (FatturatoPercentualeSection).
          La quota trattenuta entra nel Fatturato di Compensi e Report. */}
      {tiktokInfo?.has_ts_tiktok_shop_service && !tiktokInfo.registro_mensile && (
        <div className="bg-white border border-[#e9e9f1] border-l-[3px] border-l-fuchsia-400 rounded-[16px] p-[20px_22px]" data-testid="tiktok-revenue-card">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="text-[15px] font-bold text-fuchsia-800">{tiktokInfo.servizio_nome || "TikTok Shop"} — Fatturato generato al cliente</div>
              <div className="text-[13px] text-slate-600 mt-0.5 leading-[1.5]">
                Agenzia trattiene il {formatNumber(tiktokInfo.tiktok_agency_pct ?? 5)}% di questo importo → concorre al canone mensile totale e alle % di PM/venditore.
              </div>
            </div>
            {/* onBlur sul contenitore e non sul campo: NumberField usa il suo onBlur
                per tornare al valore formattato, e passarne uno lo sostituirebbe. */}
            <div className="flex items-center gap-2" onBlur={saveTiktokRevenue}>
              <div className="w-40">
                <NumberField
                  value={tiktokRevenue}
                  onChange={setTiktokRevenue}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
                  placeholder="0"
                  testId="tiktok-revenue-input"
                />
              </div>
              <button className={SECONDARY_BTN} onClick={saveTiktokRevenue} disabled={tiktokSaving} data-testid="tiktok-revenue-save">
                {tiktokSaving ? "Salvo..." : "Salva"}
              </button>
            </div>
          </div>
          {tiktokInfo && tiktokInfo.tiktok_agency_fee > 0 && (
            <div className="mt-3 flex items-center gap-4 flex-wrap text-[13px] bg-fuchsia-50/60 border border-fuchsia-200 rounded-[11px] px-3.5 py-2.5">
              <div><span className="text-slate-500">Canone:</span> <b className="scheda-font-mono">{formatCurrency(tiktokInfo.canone_mensile, { decimals: 0 })}</b></div>
              <div><span className="text-slate-500">{formatNumber(tiktokInfo.tiktok_agency_pct)}% agenzia:</span> <b className="text-fuchsia-700 scheda-font-mono">{formatCurrency(tiktokInfo.tiktok_agency_fee, { decimals: 0 })}</b></div>
              <div className="ml-auto"><span className="text-slate-500">Totale mensile:</span> <b className="text-[#0f7040] scheda-font-mono">{formatCurrency(tiktokInfo.monthly_total_agency, { decimals: 0 })}</b></div>
            </div>
          )}
        </div>
      )}

      {/* Mesi passati con pagamenti non registrati (backend/stato_pagamenti.py). */}
      <AvvisoArretrati
        arretrati={scheda.stato_pagamenti?.arretrati}
        onVaiAiPagamenti={() => document.querySelector('[data-testid="pagamenti-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
      />

      {/* Contratti firmati da 15 giorni senza il numero del documento del
          legale rappresentante (backend/routes/documento_legale.py). */}
      <AvvisoDocumentoLegale
        contratti={scheda.contratti || []}
        onUpdate={() => fetchScheda(clientId, { silent: true })}
      />

      {/* Benchmark Performance Marketing (Meta Ads) */}
      <div className="bg-white border border-[#e9e9f1] border-l-[3px] border-l-[#e0a12b] rounded-[16px] p-[20px_22px]" data-testid="benchmarks-card">
        <div className="flex items-center gap-[26px] flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <AlertTriangle className="w-[17px] h-[17px] text-[#b07314]" strokeWidth={1.9} />
              <h2 className="text-[15px] font-bold text-[#7a4e07]">Benchmark Performance Marketing (Meta Ads)</h2>
            </div>
            <p className="text-[13px] leading-[1.55] text-[#7c6440] max-w-[620px]">
              Se il CPR (costo per lead) o il CAC (costo per appuntamento) medio degli ultimi 30 giorni supera queste soglie, il cliente compare come <b className="font-bold text-[#7a4e07]">critico</b> nel Controllo di Gestione. Lascia vuoto per disattivare l&apos;allerta.
            </p>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <span className="text-[10.5px] font-semibold uppercase tracking-[.09em] text-[#9a7a3e]">CPR max</span>
              <div className="h-[42px] mt-1 rounded-[10px] border border-[#ecd9ae] bg-[#fffdf7] px-3 flex items-center gap-1.5 focus-within:border-[#e0a12b] transition-colors">
                <input
                  type="number"
                  step="0.01"
                  value={benchmarks.cpr}
                  onChange={(e) => { setBenchmarks({ ...benchmarks, cpr: e.target.value }); setBenchSaved(false); }}
                  className="w-[76px] bg-transparent outline-none text-[15px] scheda-font-mono"
                  placeholder="0"
                  data-testid="benchmark-cpr-input"
                />
                <span className="text-[12.5px] font-medium text-[#a78849] whitespace-nowrap">€/lead</span>
              </div>
            </div>
            <div>
              <span className="text-[10.5px] font-semibold uppercase tracking-[.09em] text-[#9a7a3e]">CAC max</span>
              <div className="h-[42px] mt-1 rounded-[10px] border border-[#ecd9ae] bg-[#fffdf7] px-3 flex items-center gap-1.5 focus-within:border-[#e0a12b] transition-colors">
                <input
                  type="number"
                  step="0.01"
                  value={benchmarks.cac}
                  onChange={(e) => { setBenchmarks({ ...benchmarks, cac: e.target.value }); setBenchSaved(false); }}
                  className="w-[76px] bg-transparent outline-none text-[15px] scheda-font-mono"
                  placeholder="0"
                  data-testid="benchmark-cac-input"
                />
                <span className="text-[12.5px] font-medium text-[#a78849] whitespace-nowrap">€/appt</span>
              </div>
            </div>
            <button
              onClick={saveBenchmarks}
              disabled={savingBench}
              className="h-[42px] px-[18px] rounded-[10px] text-[13.5px] font-semibold text-white transition-colors disabled:opacity-70"
              style={{ background: benchSaved ? "#0f7040" : "#b07314" }}
              data-testid="benchmark-save"
            >
              {savingBench ? "Salvo..." : benchSaved ? "Salvato" : "Salva soglie"}
            </button>
          </div>
        </div>
      </div>

      {/* Corpo: due colonne, destra sticky */}
      <div className="grid lg:grid-cols-[minmax(0,1fr)_372px] gap-[18px] items-start">
        {/* gap e non space-y: alcune sezioni rendono wrapper esterni o niente,
            e space-y perdeva lo spazio fra Commerciale e Contratto in corso. */}
        <div className="flex flex-col gap-[18px] min-w-0">
          <StrategySection clientId={clientId} strategy={scheda.strategy} onUpdate={() => fetchScheda(clientId, { silent: true })} />
          <PagamentiSection client={client} clientId={clientId} user={user} pagamenti={scheda.pagamenti || []} arretrati={scheda.arretrati_dettaglio} meseBackend={scheda.stato_pagamenti?.mese_corrente?.mese} quotaPercentuale={scheda.stato_pagamenti?.mese_corrente?.quota_percentuale} onUpdate={() => fetchScheda(clientId, { silent: true })} onPctCambiata={() => { setVersionePct(v => v + 1); fetchTiktokInfo(clientId); }} />
          {/* Dopo i Pagamenti: il fatturato inserito qui cambia il dovuto che
              mostrano, quindi a un salvataggio si ricaricano entrambi. */}
          <FatturatoPercentualeSection
            clientId={clientId}
            versione={versionePct}
            onUpdate={() => { fetchScheda(clientId, { silent: true }); fetchTiktokInfo(clientId); }}
          />
          <RevenueSection clientId={clientId} revenue={scheda.revenue} onUpdate={() => fetchScheda(clientId, { silent: true })} />
          <NotesSection clientId={clientId} notes={scheda.notes} user={user} team={scheda.team} clientName={client.name} onUpdate={() => fetchScheda(clientId, { silent: true })} />
        </div>
        <div className="flex flex-col gap-[18px] min-w-0 lg:sticky lg:top-[28px]">
          <ContrattiClienteSection
            client={client}
            contratti={scheda.contratti || []}
            rinnovoPregressoId={scheda.contratto_pregresso_rinnovato_da}
          />
          <CommercialeSection client={client} clientId={clientId} user={user} onUpdate={() => fetchScheda(clientId, { silent: true })} />
          {!client.contratto_id && !pregressoRinnovato(scheda) && (
            <ContrattoInCorsoSection client={client} clientId={clientId} user={user} onUpdate={() => fetchScheda(clientId, { silent: true })} />
          )}
          <ServiziSection client={client} clientId={clientId} user={user} onUpdate={() => fetchScheda(clientId, { silent: true })} />
          <TeamSection
            client={client}
            team={scheda.team}
            clientId={clientId}
            onUpdate={() => fetchScheda(clientId, { silent: true })}
            openSignal={teamOpenSignal}
            innerRef={teamCardRef}
          />
        </div>
      </div>
    </div>
  );
}

function FieldBlock({ label, value, editing, editValue, onChange, span, rows, placeholder }) {
  const filled = !!(value && value.trim());
  return (
    <div
      className={`p-[14px_16px] rounded-[13px] border ${filled ? "border-[#e5e5f4] bg-[#fbfbff]" : "border-[#eeeef4] bg-[#fbfbfd]"} ${span === 2 ? "sm:col-span-2" : ""}`}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${filled ? "bg-[#5b5bd6]" : "bg-[#dcdce8]"}`} />
        <span className="text-[10.5px] font-bold uppercase tracking-[.1em] text-[#565d6d]">{label}</span>
      </div>
      {editing ? (
        <Textarea
          value={editValue}
          onChange={onChange}
          rows={rows}
          placeholder={placeholder}
          className="border border-[#e4e4ed] rounded-[10px] px-3 py-2.5 text-[14px] leading-[1.5] font-medium shadow-none focus-visible:ring-0 focus:border-[#5b5bd6] focus:shadow-[0_0_0_3px_rgba(91,91,214,.14)] scheda-font-ui"
          data-testid={`strategy-field-${label}`}
        />
      ) : (
        <p className="text-[14px] leading-[1.6] text-[#3c414d] whitespace-pre-wrap">
          {value || <span className="text-[13.5px] text-[#a9adb8]">Non compilato</span>}
        </p>
      )}
    </div>
  );
}

function StrategySection({ clientId, strategy, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    meta_obiettivo: strategy?.meta_obiettivo || "",
    proposta_unica_valore: strategy?.proposta_unica_valore || "",
    obiettivo: strategy?.obiettivo || "",
    strategia_comunicazione: strategy?.strategia_comunicazione || "",
    strategia_advertising: strategy?.strategia_advertising || "",
    note: strategy?.note || "",
  });
  const [saving, setSaving] = useState(false);

  const fields = [
    { key: "meta_obiettivo", label: "Meta / Obiettivo del Cliente", span: 2, rows: 3, placeholder: "Qual è il risultato che il cliente vuole ottenere?" },
    { key: "proposta_unica_valore", label: "Proposta Unica di Valore (PUV)", span: 2, rows: 3, placeholder: "Perché dovrebbero scegliere lui" },
    { key: "obiettivo", label: "Obiettivo", span: 1, rows: 2, placeholder: "Obiettivo misurabile del periodo" },
    { key: "strategia_comunicazione", label: "Strategia Comunicazione", span: 1, rows: 2, placeholder: "Tono, canali, messaggi" },
    { key: "strategia_advertising", label: "Strategia Advertising", span: 1, rows: 2, placeholder: "Campagne, budget, target" },
    { key: "note", label: "Note / Attenzioni", span: 1, rows: 2, placeholder: "Cose da ricordare su questo cliente" },
  ];

  const filledCount = fields.filter(f => form[f.key]?.trim()).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/strategy`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success("Strategia salvata");
        setEditing(false);
        onUpdate();
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      icon={Target}
      iconBg="#eeeefc"
      iconColor="#5b5bd6"
      title="Strategia"
      count={`${filledCount}/6`}
      testId="strategy-section"
      action={
        !editing ? (
          <button className={SECONDARY_BTN} onClick={() => setEditing(true)} data-testid="edit-strategy-btn">
            <Pencil className="w-3 h-3" /> Modifica
          </button>
        ) : (
          <div className="flex gap-2">
            <button className={GHOST_BTN} onClick={() => setEditing(false)}>Annulla</button>
            <button className={PRIMARY_BTN} onClick={handleSave} disabled={saving} data-testid="save-strategy-btn">
              {saving ? "Salvo..." : "Salva"}
            </button>
          </div>
        )
      }
    >
      <div className="h-1 rounded-full bg-[#f0f0f6] mb-[18px] overflow-hidden">
        <div
          className="h-full rounded-full bg-[#5b5bd6] transition-[width]"
          style={{ width: `${(filledCount / 6) * 100}%`, transitionDuration: "450ms", transitionTimingFunction: "cubic-bezier(.22,.7,.3,1)" }}
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-[14px]">
        {fields.map(f => (
          <FieldBlock
            key={f.key}
            label={f.label}
            span={f.span}
            rows={f.rows}
            placeholder={f.placeholder}
            value={form[f.key]}
            editing={editing}
            editValue={form[f.key]}
            onChange={(e) => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
          />
        ))}
      </div>
    </SectionCard>
  );
}

function ServiziSection({ client, clientId, user, onUpdate }) {
  // Pacchetti dal Catalogo Servizi (Impostazioni), uno per servizio — per i
  // clienti nati prima del wizard Contratti, che non ne hanno mai ricevuto
  // uno, e per aggiungere un servizio a un cliente già attivo (vedi
  // backend/routes/pacchetto_cliente.py). Perimetro più largo del Catalogo:
  // tutti tranne i ruoli di sola produzione.
  const canManagePacchetto = puoAggiungereServizi(user);
  // Cliente non nato da un contratto: il backend segna i servizi aggiunti con
  // l'attivazione già saldata (routes/pacchetto_cliente.py).
  const clienteGiaAttivo = !client?.contratto_id;

  const [catalogo, setCatalogo] = useState([]);
  const [catalogoLoading, setCatalogoLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [servizioScelto, setServizioScelto] = useState("");
  const [pacchettoSaving, setPacchettoSaving] = useState(false);
  const [pacchettoDaRimuovere, setPacchettoDaRimuovere] = useState(null);

  const ADDON_VUOTO = { label: "", ricorrenza: "una_tantum", tipo_valore: "importo", valore: "" };
  const [addonFormPacchetto, setAddonFormPacchetto] = useState(null); // pacchetto_id col form aperto
  const [addonForm, setAddonForm] = useState(ADDON_VUOTO);
  const [addonSaving, setAddonSaving] = useState(false);
  const [addonDeletingId, setAddonDeletingId] = useState(null);
  // { pacchetto, addon } in attesa di conferma: la X è piccola e vicina
  // all'importo, un clic accidentale toglierebbe una voce dai dovuti.
  const [addonDaEliminare, setAddonDaEliminare] = useState(null);

  // Il backend restituisce sempre la lista, anche per i clienti nel formato a
  // pacchetto singolo (backend/pacchetti.py, pacchetti_cliente).
  const pacchetti = client?.pacchetti || [];
  const hasPacchetto = pacchetti.length > 0;
  const serviziGiaPresenti = new Set(pacchetti.map(p => p.servizio_id).filter(Boolean));
  const catalogoDisponibile = catalogo.filter(s => !serviziGiaPresenti.has(s.servizio_id));

  const apriPicker = async () => {
    setServizioScelto("");
    setPickerOpen(true);
    setCatalogoLoading(true);
    try {
      const res = await apiFetch("/catalogo-servizi");
      if (res.ok) {
        const data = await res.json();
        setCatalogo(data.items || []);
      }
    } finally {
      setCatalogoLoading(false);
    }
  };
  const chiudiPicker = () => { setPickerOpen(false); setServizioScelto(""); };

  const aggiungiPacchetto = async () => {
    if (!servizioScelto) return;
    setPacchettoSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/pacchetti`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ servizio_id: servizioScelto }),
      });
      if (res.ok) {
        toast.success("Servizio aggiunto");
        chiudiPicker();
        onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nell'aggiunta del servizio");
      }
    } catch (error) {
      toast.error("Errore nell'aggiunta del servizio");
    } finally {
      setPacchettoSaving(false);
    }
  };

  const rimuoviPacchetto = async () => {
    if (!pacchettoDaRimuovere) return;
    setPacchettoSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/pacchetti/${pacchettoDaRimuovere.pacchetto_id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Servizio rimosso");
        setPacchettoDaRimuovere(null);
        onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nella rimozione del servizio");
      }
    } catch (error) {
      toast.error("Errore nella rimozione del servizio");
    } finally {
      setPacchettoSaving(false);
    }
  };

  // Niente reset qui: i campi restano visibili mentre il pannello si richiude.
  // Il form si azzera all'apertura.
  const chiudiAddonForm = () => setAddonFormPacchetto(null);

  const aggiungiAddonCliente = async (pacchettoId) => {
    if (!addonForm.label.trim()) { toast.error("Inserisci una descrizione per l'addon"); return; }
    const valoreNum = Number(addonForm.valore);
    if (!(valoreNum >= 0)) { toast.error("Inserisci un valore valido"); return; }
    if (addonForm.tipo_valore === "percentuale" && valoreNum > 100) { toast.error("Una percentuale non può superare il 100%"); return; }
    setAddonSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/pacchetti/${pacchettoId}/addons`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: addonForm.label.trim(),
          ricorrenza: addonForm.ricorrenza,
          tipo_valore: addonForm.tipo_valore,
          valore: valoreNum,
        }),
      });
      if (res.ok) {
        toast.success("Addon aggiunto");
        chiudiAddonForm();
        onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nell'aggiunta dell'addon");
      }
    } catch (error) {
      toast.error("Errore nell'aggiunta dell'addon");
    } finally {
      setAddonSaving(false);
    }
  };

  const eliminaAddonCliente = async (pacchettoId, addonId) => {
    setAddonDeletingId(addonId);
    try {
      const res = await apiFetch(`/clients/${clientId}/pacchetti/${pacchettoId}/addons/${addonId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Addon eliminato");
        setAddonDaEliminare(null);
        onUpdate();
      } else {
        toast.error("Errore nell'eliminazione dell'addon");
      }
    } finally {
      setAddonDeletingId(null);
    }
  };

  // Funzione di render, non componente: vedi il commento su renderRegisterForm
  // in PagamentiSection, components/PagamentiCliente.jsx (un componente interno perderebbe il focus degli input).
  const renderPacchetto = (pk) => {
    const addons = pk.addons || [];
    const formAperto = addonFormPacchetto === pk.pacchetto_id;
    const canonePk = canoneServizi({ pacchetti: [pk] });
    return (
      <div key={pk.pacchetto_id} className="p-[18px] rounded-[14px] border border-[#ededf4] bg-[#f8f8fc] flex flex-col gap-[14px]" data-testid="pacchetto-servizio">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[.1em] text-[#9095a1] mb-0.5">Pacchetto</p>
            <h3 className="text-[18px] font-bold text-[#15161c]">{pk.nome || "—"}</h3>
            {pk.descrizione && (
              <p className="text-[13px] leading-[1.5] text-[#6b7280] mt-0.5 whitespace-pre-wrap">{pk.descrizione}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className="h-6 px-2.5 rounded-full bg-[#e7f7ee] text-[#0f7040] text-[11px] font-semibold">In corso</span>
            {canManagePacchetto && (
              <button className={GHOST_BTN} onClick={() => setPacchettoDaRimuovere(pk)} data-testid={`rimuovi-pacchetto-btn-${pk.pacchetto_id}`}>
                Rimuovi
              </button>
            )}
          </div>
        </div>

        {/* In evidenza il canone mensile del servizio, non l'attivazione:
            è ciò che il cliente deve dare ogni mese. L'attivazione resta nei
            Pagamenti. */}
        {canonePk.haMensili && (canonePk.fisso > 0 || canonePk.percentuale > 0) && (
          <div className="p-[12px_14px] rounded-[11px] bg-white border border-[#e9e9f1] flex items-baseline gap-2 flex-wrap" data-testid="canone-mensile-pacchetto">
            <span className="text-[22px] font-bold scheda-font-mono text-[#0f7040]">
              {[
                canonePk.fisso > 0 ? formatCurrency(canonePk.fisso, { decimals: 0 }) : null,
                canonePk.percentuale > 0 ? `${formatNumber(canonePk.percentuale)}%` : null,
              ].filter(Boolean).join(" + ")}
            </span>
            <span className="text-[12.5px] font-medium text-[#6b7280]">al mese</span>
          </div>
        )}

        {(addons.length > 0 || canManagePacchetto) && (
          <div className="flex flex-col gap-3">
            {addons.length > 0 ? (
              <div className="rounded-[11px] border border-[#e9e9f1] overflow-hidden divide-y divide-[#e9e9f1] bg-white">
                {addons.map((a, i) => (
                  <div key={a.addon_id || i} className="flex items-center justify-between gap-2 px-3.5 py-2.5">
                    <span className="text-[13px] font-medium text-[#565d6d]">{a.label}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[13.5px] font-semibold scheda-font-mono text-[#15161c]">
                        {a.tipo_valore === "percentuale" ? `${formatNumber(a.valore)}%` : formatCurrency(a.valore, { decimals: 0 })}
                        {a.ricorrenza === "mensile" ? " / mese" : ""}
                      </span>
                      {canManagePacchetto && (
                        <button
                          onClick={() => setAddonDaEliminare({ pacchetto: pk, addon: a })}
                          disabled={addonDeletingId === a.addon_id}
                          className="h-6 w-6 rounded-[8px] flex items-center justify-center text-[#9095a1] hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                          data-testid={`elimina-addon-cliente-${a.addon_id || i}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState boxed>Nessun addon per questo pacchetto.</EmptyState>
            )}

            {canManagePacchetto && (
              // Contenitore senza gap: il pannello chiuso ha altezza zero e non
              // deve aggiungere spazio sopra il pulsante.
              <div className="flex flex-col">
                {/* Sempre montato: l'altezza si apre e si chiude con la transizione
                    di .addon-form (index.css). inert da chiuso, così il Tab non
                    entra nei campi nascosti. */}
                <div className="addon-form" data-aperto={formAperto} inert={!formAperto}>
                  <div>
                    <div className="p-4 rounded-[14px] border border-[#dcdcf5] bg-gradient-to-b from-[#f7f7fe] to-white flex flex-col gap-4" data-testid={formAperto ? "addon-cliente-form" : undefined}>
                      <div className="addon-form-strato flex items-center justify-between" style={{ "--i": 0 }}>
                        <p className="text-[14px] font-bold text-[#15161c]">Nuovo addon</p>
                        <button type="button" onClick={chiudiAddonForm} aria-label="Chiudi" className="h-7 w-7 rounded-[9px] flex items-center justify-center text-[#9095a1] hover:text-[#15161c] hover:bg-white transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="addon-form-strato" style={{ "--i": 1 }}>
                        <Input
                          value={addonForm.label}
                          onChange={(e) => setAddonForm(prev => ({ ...prev, label: e.target.value }))}
                          placeholder="Descrizione, es. doppio girato"
                          className={`${FIELD_CLS} w-full bg-white`}
                          data-testid="addon-cliente-label-input"
                        />
                      </div>

                      {/* La spiegazione sotto ogni scelta è la regola di
                          backend/stato_pagamenti.py (dovuto_dal dell'addon). */}
                      <div className="addon-form-strato grid grid-cols-2 gap-2" style={{ "--i": 2 }} role="radiogroup" aria-label="Ricorrenza">
                        {[
                          { v: "una_tantum", l: "Una tantum", d: "Solo questo mese", Icon: Zap },
                          { v: "mensile", l: "Mensile", d: "Da questo mese in poi", Icon: Repeat },
                        ].map(({ v, l, d, Icon }) => {
                          const attivo = addonForm.ricorrenza === v;
                          return (
                            <button
                              key={v}
                              type="button"
                              role="radio"
                              aria-checked={attivo}
                              onClick={() => setAddonForm(prev => ({ ...prev, ricorrenza: v }))}
                              className={`addon-form-scelta relative text-left flex items-center gap-2 py-2 px-2.5 rounded-[10px] border transition-all duration-200 ${attivo ? "border-[#5b5bd6] bg-[#eeeefc] shadow-[0_0_0_3px_rgba(91,91,214,.12)]" : "border-[#e4e4ed] bg-white hover:border-[#c9c9f0] hover:-translate-y-px"}`}
                              data-testid={`addon-cliente-ricorrenza-${v}`}
                            >
                              <span className={`w-6 h-6 rounded-[7px] flex items-center justify-center shrink-0 transition-colors ${attivo ? "bg-[#5b5bd6] text-white" : "bg-[#f3f3f8] text-[#6b7280]"}`}>
                                <Icon className="w-3 h-3" />
                              </span>
                              <span className="min-w-0">
                                <span className={`block text-[12px] font-semibold leading-tight ${attivo ? "text-[#15161c]" : "text-[#565d6d]"}`}>{l}</span>
                                <span className="block text-[10.5px] font-medium text-[#9095a1] leading-tight mt-0.5">{d}</span>
                              </span>
                              {attivo && <Check className="absolute top-1.5 right-1.5 w-3 h-3 text-[#5b5bd6]" />}
                            </button>
                          );
                        })}
                      </div>

                      <div className="addon-form-strato flex items-center gap-2" style={{ "--i": 3 }}>
                        <div className="flex-1 min-w-0">
                          <NumberField
                            value={addonForm.valore}
                            onChange={(v) => setAddonForm(prev => ({ ...prev, valore: v }))}
                            unit={addonForm.tipo_valore === "percentuale"
                              ? (addonForm.ricorrenza === "mensile" ? "%/mese" : "%")
                              : "€"}
                            placeholder={addonForm.tipo_valore === "percentuale" ? "0" : "Importo"}
                            testId="addon-cliente-valore-input"
                          />
                        </div>
                        <SegmentedControl
                          value={addonForm.tipo_valore}
                          onChange={(v) => setAddonForm(prev => ({ ...prev, tipo_valore: v }))}
                          options={[
                            { value: "importo", label: "€", testId: "addon-cliente-tipo-importo" },
                            { value: "percentuale", label: "%", testId: "addon-cliente-tipo-percentuale" },
                          ]}
                        />
                      </div>

                      <div className="addon-form-strato flex gap-2 justify-end" style={{ "--i": 4 }}>
                        <button className={GHOST_BTN} onClick={chiudiAddonForm}>
                          Annulla
                        </button>
                        <button className={PRIMARY_BTN} onClick={() => aggiungiAddonCliente(pk.pacchetto_id)} disabled={addonSaving} data-testid="salva-addon-cliente-btn">
                          {addonSaving ? "Salvo..." : "Aggiungi addon"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {!formAperto && (
                  <button className={`${GHOST_BTN} self-start`} onClick={() => { setAddonForm(ADDON_VUOTO); setAddonFormPacchetto(pk.pacchetto_id); }} data-testid="aggiungi-addon-cliente-btn">
                    <Plus className="w-3 h-3" /> Aggiungi addon
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <SectionCard
      icon={Package}
      iconBg="#e4f6f3"
      iconColor="#0d8377"
      title="Servizi"
      dense
      testId="servizi-section"
    >
      {/* Le vecchie tag per reparto (client.servizi_reparto) non si mostrano né
          si modificano più: i servizi del cliente sono i pacchetti del Catalogo
          Servizi, gli unici che generano importi nei Pagamenti. Il campo resta
          nel database per chi lo legge ancora lato backend. */}
      <div className="flex flex-col gap-3">
        {hasPacchetto ? (
          <>
            {pacchetti.map(renderPacchetto)}
            {canManagePacchetto && (
              <button className={`${SECONDARY_BTN} w-full`} onClick={apriPicker} data-testid="aggiungi-pacchetto-btn">
                <Plus className="w-3 h-3" /> Aggiungi servizio
              </button>
            )}
          </>
        ) : canManagePacchetto ? (
          <div className="flex flex-col gap-3">
            <div className="p-4 rounded-[13px] border border-dashed border-[#dcdce8] bg-[#fbfbfd]">
              <p className="text-[13.5px] leading-[1.5] text-[#6b7280]">
                Nessun servizio collegato. Aggiungine uno dal catalogo per farlo comparire anche tra i Pagamenti.
              </p>
            </div>
            <button className={`${PRIMARY_BTN} w-full`} onClick={apriPicker} data-testid="attacca-pacchetto-btn">
              <Plus className="w-3 h-3" /> Aggiungi servizio
            </button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">Nessun servizio configurato</p>
        )}
      </div>

      <Dialog open={pickerOpen} onOpenChange={(o) => { if (!o) chiudiPicker(); }}>
        <DialogContent className={`${DIALOG_CONTENT_CLS} sm:max-w-md`} data-testid="pacchetto-picker-dialog">
          <h3 className="text-[16px] font-bold text-[#15161c]">
            Aggiungi servizio
          </h3>
          <p className="text-[13px] text-[#9095a1] mt-1">
            {clienteGiaAttivo
              ? "Scegli un servizio configurato in Impostazioni → Catalogo Servizi. Il cliente è già attivo: l'attivazione si considera saldata e nei Pagamenti partono subito i mensili."
              : "Scegli un servizio configurato in Impostazioni → Catalogo Servizi. Avrà la sua attivazione nei Pagamenti."}
          </p>
          <div className="mt-4">
            {catalogoLoading ? (
              <p className="text-[13px] text-[#9095a1]">Caricamento…</p>
            ) : catalogo.length === 0 ? (
              <EmptyState boxed>Nessun servizio configurato in Impostazioni.</EmptyState>
            ) : catalogoDisponibile.length === 0 ? (
              <EmptyState boxed>Il cliente ha già tutti i servizi del catalogo.</EmptyState>
            ) : (
              <Select value={servizioScelto} onValueChange={setServizioScelto}>
                <SelectTrigger className={`${FIELD_CLS} w-full`} data-testid="pacchetto-select">
                  <SelectValue placeholder="Seleziona un servizio" />
                </SelectTrigger>
                <SelectContent className="rounded-[14px] border-[#e9e9f1] shadow-lg p-1.5 scheda-font-ui">
                  {catalogoDisponibile.map(s => (
                    <SelectItem
                      key={s.servizio_id}
                      value={s.servizio_id}
                      className="rounded-[10px] py-1.5 pl-2 pr-8 my-0.5 cursor-pointer focus:bg-[#f6f6fb] data-[state=checked]:bg-[#eeeefc]"
                    >
                      <span className="text-[13.5px] text-[#15161c]">{s.nome}</span>
                      <span className="text-[12px] text-[#9095a1] ml-1.5 scheda-font-mono">
                        {formatCurrency(s.importo_attivazione, { decimals: 0 })}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex gap-2 justify-end mt-5">
            <button className={GHOST_BTN} onClick={chiudiPicker}>Annulla</button>
            <button
              className={PRIMARY_BTN}
              onClick={aggiungiPacchetto}
              disabled={!servizioScelto || pacchettoSaving}
              data-testid="conferma-pacchetto-btn"
            >
              {pacchettoSaving ? "Salvo..." : "Conferma"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pacchettoDaRimuovere} onOpenChange={(o) => { if (!o && !pacchettoSaving) setPacchettoDaRimuovere(null); }}>
        <AlertDialogContent className={DIALOG_CONTENT_CLS} data-testid="rimuovi-pacchetto-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#15161c]">Rimuovere {pacchettoDaRimuovere?.nome || "il pacchetto"} da {client?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Attivazione e addon di questo servizio non saranno più attesi nei Pagamenti. I pagamenti già registrati restano nello storico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pacchettoSaving}>Annulla</AlertDialogCancel>
            <button
              className={PRIMARY_BTN}
              onClick={rimuoviPacchetto}
              disabled={pacchettoSaving}
              data-testid="conferma-rimozione-pacchetto-btn"
            >
              {pacchettoSaving ? "Salvo..." : "Rimuovi"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!addonDaEliminare} onOpenChange={(o) => { if (!o && !addonDeletingId) setAddonDaEliminare(null); }}>
        <AlertDialogContent className={DIALOG_CONTENT_CLS} data-testid="elimina-addon-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#15161c]">
              Eliminare l'addon {addonDaEliminare?.addon?.label ? `«${addonDaEliminare.addon.label}»` : ""} da {addonDaEliminare?.pacchetto?.nome || "questo pacchetto"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {addonDaEliminare?.addon && (
                <>
                  Importo: <strong className="text-[#15161c]">
                    {addonDaEliminare.addon.tipo_valore === "percentuale"
                      ? `${formatNumber(addonDaEliminare.addon.valore)}%`
                      : formatCurrency(addonDaEliminare.addon.valore, { decimals: 0 })}
                    {addonDaEliminare.addon.ricorrenza === "mensile" ? " / mese" : " una tantum"}
                  </strong>.{" "}
                </>
              )}
              Da questo momento non sarà più atteso nei Pagamenti di {client?.name}. I pagamenti già registrati restano nello storico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!addonDeletingId}>Annulla</AlertDialogCancel>
            <button
              className={PRIMARY_BTN}
              onClick={() => eliminaAddonCliente(addonDaEliminare.pacchetto.pacchetto_id, addonDaEliminare.addon.addon_id)}
              disabled={!!addonDeletingId}
              data-testid="conferma-elimina-addon-btn"
            >
              {addonDeletingId ? "Elimino..." : "Elimina"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}

// Clienti nati da un contratto: il team si assegna solo dopo l'incasso
// dell'attivazione. Il backend (assign-reparto-staff) applica la stessa regola.

// Ruoli di sola produzione (SMM, Content Creator = "videomaker", Videomaker =
// "filmmaker", Editor): non gestiscono servizi e addon del cliente. Basta un
// ruolo, principale o secondario, fuori da questo elenco. Stesso elenco in
// RUOLI_ESCLUSI, backend/routes/pacchetto_cliente.py, che protegge anche il
// Contratto in corso (routes/contratto_pregresso.py).
const RUOLI_SOLO_PRODUZIONE = ["smm", "videomaker", "filmmaker", "editor"];
function puoAggiungereServizi(user) {
  if (user?.email === "info@diecixcento.com" || user?.is_responsabile_operativo) return true;
  const ruoli = [user?.role, ...(user?.secondary_roles || [])].filter(Boolean);
  return ruoli.some(r => !RUOLI_SOLO_PRODUZIONE.includes(r));
}

const toIsoLocale = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Scadenza a N mesi da oggi, come fa il wizard (relativedelta sul backend).
const scadenzaTraMesi = (mesi) => {
  const d = new Date();
  d.setMonth(d.getMonth() + mesi);
  return toIsoLocale(d);
};

// Mesi interi fra oggi e la data: serve solo a ripopolare il campo "mesi"
// quando si sceglie la data dal calendario, la fonte di verità resta la data.
const mesiInteriFinoA = (iso) => {
  const oggi = new Date();
  const d = new Date(`${iso}T00:00:00`);
  let mesi = (d.getFullYear() - oggi.getFullYear()) * 12 + (d.getMonth() - oggi.getMonth());
  if (d.getDate() < oggi.getDate()) mesi -= 1;
  return Math.max(0, mesi);
};

const MESI_RESIDUI_MAX = 60; // come ORIZZONTE_MAX_MESI in routes/contratto_pregresso.py

// Clienti nati prima del wizard Contratti: non hanno un contratto collegato da
// cui leggere servizi e durata. Qui si dichiarano a posteriori — i servizi
// diventano pacchetti del catalogo (con l'attivazione considerata già pagata)
// e la scadenza alimenta la barra "tempo restante" nella lista clienti.
function ContrattoInCorsoSection({ client, clientId, user, onUpdate }) {
  const canManage = puoAggiungereServizi(user);
  const pregresso = client?.contratto_pregresso || null;
  const pacchettiCatalogo = (client?.pacchetti || []).filter(p => p.servizio_id);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [catalogoLoading, setCatalogoLoading] = useState(false);
  const [selezionati, setSelezionati] = useState([]);
  const [mesi, setMesi] = useState("");
  const [scadenza, setScadenza] = useState("");

  const apriModifica = async () => {
    setSelezionati(pacchettiCatalogo.map(p => p.servizio_id));
    const scadenzaAttuale = pregresso?.data_scadenza ? String(pregresso.data_scadenza).slice(0, 10) : "";
    setScadenza(scadenzaAttuale);
    setMesi(scadenzaAttuale ? String(mesiInteriFinoA(scadenzaAttuale)) : "");
    setEditing(true);
    setCatalogoLoading(true);
    try {
      const res = await apiFetch("/catalogo-servizi");
      if (res.ok) setCatalogo((await res.json()).items || []);
    } finally {
      setCatalogoLoading(false);
    }
  };

  const toggle = (servizioId) => setSelezionati(prev => (
    prev.includes(servizioId) ? prev.filter(id => id !== servizioId) : [...prev, servizioId]
  ));

  const cambiaMesi = (valore) => {
    setMesi(valore);
    const n = parseInt(valore, 10);
    setScadenza(n >= 1 && n <= MESI_RESIDUI_MAX ? scadenzaTraMesi(n) : "");
  };

  const cambiaScadenza = (iso) => {
    setScadenza(iso);
    setMesi(String(mesiInteriFinoA(iso)));
  };

  const oggiIso = toIsoLocale(new Date());
  const scadenzaValida = !!scadenza && scadenza > oggiIso;
  const daRimuovere = pacchettiCatalogo.filter(p => !selezionati.includes(p.servizio_id));

  const salva = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/contratto-pregresso`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ servizio_ids: selezionati, data_scadenza: scadenza }),
      });
      if (res.ok) {
        toast.success("Contratto in corso aggiornato");
        setEditing(false);
        onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Chi non può modificare non ha nulla da fare con un pannello vuoto.
  if (!pregresso && !canManage) return null;

  const tile = ICON_TILES.viola;
  const renderVista = () => {
    if (!pregresso) {
      return (
        <div className="space-y-3">
          <EmptyState boxed>
            Cliente precedente ai contratti firmati nell&apos;app: indica i servizi in corso e quanto manca alla scadenza.
          </EmptyState>
          <button className={`${PRIMARY_BTN} w-full`} onClick={apriModifica} data-testid="configura-contratto-pregresso-btn">
            Configura contratto in corso
          </button>
        </div>
      );
    }
    const dataScadenza = new Date(`${String(pregresso.data_scadenza).slice(0, 10)}T00:00:00`);
    const inizio = pregresso.data_inizio ? new Date(pregresso.data_inizio) : null;
    const adesso = new Date();
    const giorni = Math.ceil((dataScadenza - adesso) / (1000 * 60 * 60 * 24));
    const totale = inizio ? dataScadenza - inizio : 0;
    const trascorso = totale > 0 ? Math.min(1, Math.max(0, (adesso - inizio) / totale)) : (giorni < 0 ? 1 : 0);
    const tono = giorni < 0 ? "#c0392b" : giorni <= 30 ? "#b45309" : "#0f7040";
    const riempimento = giorni < 0 ? "#c0392b" : giorni <= 30 ? "#f59e0b" : "#12a05c";
    const etichetta = giorni < 0 ? `Scaduto da ${formatNumber(Math.abs(giorni))} gg` : giorni === 0 ? "Scade oggi" : `${formatNumber(giorni)} gg alla scadenza`;
    return (
      <div className="flex flex-col gap-3">
        <div className="p-[12px_14px] rounded-[11px] bg-[#f8f8fc] border border-[#ededf4]">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <span className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#9095a1]">Scadenza</span>
            <span className="text-[15px] font-bold scheda-font-mono text-[#15161c]">{dataScadenza.toLocaleDateString("it-IT")}</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#e9e9f1] overflow-hidden mt-2">
            <div className="h-full rounded-full" style={{ width: `${Math.round(trascorso * 100)}%`, background: riempimento }} />
          </div>
          <span className="text-[12px] font-semibold mt-1 inline-block" style={{ color: tono }}>{etichetta}</span>
        </div>
        {pacchettiCatalogo.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {pacchettiCatalogo.map(p => <Chip key={p.pacchetto_id}>{p.nome}</Chip>)}
          </div>
        ) : (
          <EmptyState>Nessun servizio del catalogo collegato.</EmptyState>
        )}
      </div>
    );
  };

  return (
    <SectionCard
      icon={CalendarClock}
      iconBg={tile.bg}
      iconColor={tile.color}
      title="Contratto in corso"
      dense
      testId="contratto-pregresso-section"
      action={
        canManage && pregresso && !editing ? (
          <button className={SECONDARY_BTN} onClick={apriModifica} data-testid="modifica-contratto-pregresso-btn">
            <Pencil className="w-3 h-3" /> Modifica
          </button>
        ) : null
      }
    >
      {!editing ? renderVista() : (
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#9095a1] mb-2">Servizi</p>
            {catalogoLoading ? (
              <p className="text-[13px] text-[#9095a1]">Caricamento…</p>
            ) : catalogo.length === 0 ? (
              <EmptyState boxed>Nessun servizio configurato in Impostazioni.</EmptyState>
            ) : (
              <div className="flex flex-col gap-1.5">
                {catalogo.map(s => {
                  const attivo = selezionati.includes(s.servizio_id);
                  return (
                    <button
                      key={s.servizio_id}
                      type="button"
                      onClick={() => toggle(s.servizio_id)}
                      className={`w-full px-3 py-2 rounded-[10px] border text-left flex items-center gap-2.5 transition-colors ${
                        attivo ? "bg-[#eeeefc] border-[#c9c9f0]" : "bg-white border-[#e4e4ed] hover:border-[#c9c9f0]"
                      }`}
                      data-testid={`contratto-pregresso-servizio-${s.servizio_id}`}
                    >
                      <span className={`w-4 h-4 rounded-[5px] border flex items-center justify-center shrink-0 ${
                        attivo ? "bg-[#5b5bd6] border-[#5b5bd6]" : "bg-white border-[#dcdce8]"
                      }`}>
                        {attivo && <Check className="w-3 h-3 text-white" strokeWidth={2.5} />}
                      </span>
                      <span className="text-[13.5px] text-[#15161c] flex-1 min-w-0">{s.nome}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#9095a1] mb-2">Durata residua</p>
            <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-2 items-center">
              <div className="relative">
                <Input
                  type="number"
                  min={1}
                  max={MESI_RESIDUI_MAX}
                  value={mesi}
                  onChange={(e) => cambiaMesi(e.target.value)}
                  className={`${FIELD_CLS} scheda-font-mono pr-12 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
                  placeholder="0"
                  data-testid="contratto-pregresso-mesi"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-[#9095a1] pointer-events-none">mesi</span>
              </div>
              <DatePicker
                value={scadenza}
                onChange={cambiaScadenza}
                className={FIELD_CLS}
                placeholder="Data di scadenza"
                data-testid="contratto-pregresso-scadenza"
              />
            </div>
            <p className="text-[12px] text-[#9095a1] mt-1.5">
              Indica i mesi che mancano o scegli direttamente la data di scadenza.
            </p>
          </div>

          <p className="text-[12.5px] leading-[1.5] text-[#6b7280] p-[10px_12px] rounded-[10px] bg-[#f8f8fc] border border-[#ededf4]">
            L&apos;attivazione dei servizi scelti si considera già pagata: nei Pagamenti compaiono solo gli addon, dovuti da subito.
          </p>
          {daRimuovere.length > 0 && (
            <p className="text-[12.5px] leading-[1.5] text-[#b45309] p-[10px_12px] rounded-[10px] bg-[#fef3c7]" data-testid="contratto-pregresso-avviso-rimozione">
              Verranno rimossi {daRimuovere.map(p => p.nome).join(", ")}, con i loro addon. I pagamenti già registrati restano nello storico.
            </p>
          )}

          <div className="flex gap-2 justify-end">
            <button className={GHOST_BTN} onClick={() => setEditing(false)} disabled={saving}>Annulla</button>
            <button
              className={PRIMARY_BTN}
              onClick={salva}
              disabled={saving || selezionati.length === 0 || !scadenzaValida}
              data-testid="salva-contratto-pregresso-btn"
            >
              {saving ? "Salvo..." : "Salva"}
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// Contratto pregresso superato da un rinnovo firmato nell'app: da lì
// servizi e scadenza stanno sul contratto di rinnovo, il pannello
// "Contratto in corso" non serve più (il backend ne rifiuta la modifica).
function pregressoRinnovato(scheda) {
  const id = scheda?.contratto_pregresso_rinnovato_da;
  return !!id && (scheda.contratti || []).some(c => c.contratto_id === id && c.stato_firma === "firmato");
}

// Contratti firmati da più di 15 giorni senza tipo o numero del documento del
// legale rappresentante: il wizard li lascia facoltativi, ma all'amministrazione
// servono. Il contratto firmato è bloccato, quindi si inseriscono da qui
// (PATCH /contratti/{id}/documento-legale, solo i campi che mancano); il PDF
// firmato resta quello.
// Stessi valori di DOCUMENTI in ContrattiPage.jsx.
const TIPI_DOCUMENTO = ["Carta d'identità", "Patente", "Passaporto"];

function AvvisoDocumentoLegale({ contratti, onUpdate }) {
  const [valori, setValori] = useState({});
  const [saving, setSaving] = useState(null);
  const daIntegrare = contratti.filter(c => c.documento_legale?.in_ritardo);
  if (!daIntegrare.length) return null;

  const valore = (id, campo) => (valori[id]?.[campo] || "").trim();
  const imposta = (id, campo, v) => setValori({ ...valori, [id]: { ...valori[id], [campo]: v } });

  const salva = async (c) => {
    const tipo = valore(c.contratto_id, "tipo");
    const numero = valore(c.contratto_id, "numero");
    if (!tipo && !numero) return;
    setSaving(c.contratto_id);
    try {
      const r = await apiFetch(`/contratti/${c.contratto_id}/documento-legale`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ documento_tipo: tipo || null, documento_numero: numero || null }),
      });
      if (r.ok) {
        const res = await r.json().catch(() => ({}));
        toast.success(res.mancano?.length ? `Salvato: manca ancora il ${res.mancano[0]}` : "Dati del documento salvati");
        setValori({ ...valori, [c.contratto_id]: {} });
        onUpdate();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } finally {
      setSaving(null);
    }
  };

  return (
    <div
      className="flex items-start gap-3 px-5 py-4 rounded-[16px] bg-[#fffbeb] border border-[#f5deb0] border-l-[3px] border-l-[#b45309] scheda-font-ui"
      role="alert"
      data-testid="avviso-documento-legale"
    >
      <AlertTriangle className="w-[18px] h-[18px] text-[#b45309] shrink-0 mt-0.5" strokeWidth={2} />
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <span className="text-[14.5px] font-bold text-[#7a4e07]">
          Mancano i dati del documento del legale rappresentante
        </span>
        {daIntegrare.map(c => {
          const etichetta = ETICHETTE_TIPO_CONTRATTO[tipoVisibile(c)] || ETICHETTE_TIPO_CONTRATTO.primo;
          const firmato = c.documento_legale.firmato_at ? new Date(c.documento_legale.firmato_at).toLocaleDateString("it-IT") : null;
          const mancano = c.documento_legale.mancano || [];
          const cosa = mancano.length === 2 ? "tipo e numero del documento non sono stati inseriti" : `il ${mancano[0]} del documento non è stato inserito`;
          const pronto = !!(valore(c.contratto_id, "tipo") || valore(c.contratto_id, "numero"));
          return (
            <div key={c.contratto_id} className="flex items-center gap-2 flex-wrap">
              <span className="text-[13px] text-[#7c6440] flex-1 min-w-[200px]">
                <b className="font-semibold text-[#7a4e07]">{etichetta}</b>
                {firmato ? ` firmato il ${firmato}` : " firmato"}: {cosa}.
              </span>
              {c.documento_legale.puo_integrare && (
                <div className="flex items-center gap-2 flex-wrap">
                  {mancano.includes("tipo") && (
                    <Select value={valori[c.contratto_id]?.tipo || ""} onValueChange={(v) => imposta(c.contratto_id, "tipo", v)}>
                      <SelectTrigger className={`${FIELD_CLS} h-8 w-[170px]`} data-testid={`documento-legale-tipo-${c.contratto_id}`}>
                        <SelectValue placeholder="Tipo documento" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPI_DOCUMENTO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {mancano.includes("numero") && (
                    <input
                      value={valori[c.contratto_id]?.numero || ""}
                      onChange={(e) => imposta(c.contratto_id, "numero", e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") salva(c); }}
                      placeholder="Numero documento"
                      className={`${FIELD_CLS} h-8 w-[170px] scheda-font-mono`}
                      data-testid={`documento-legale-input-${c.contratto_id}`}
                    />
                  )}
                  <button
                    type="button"
                    className={PRIMARY_BTN_SM}
                    disabled={saving === c.contratto_id || !pronto}
                    onClick={() => salva(c)}
                    data-testid={`documento-legale-salva-${c.contratto_id}`}
                  >
                    {saving === c.contratto_id ? "Salvo..." : "Salva"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        <span className="text-[12px] text-[#9a7a3e]">
          Il contratto firmato non cambia: i dati vengono archiviati nel contratto.
        </span>
      </div>
    </div>
  );
}

const ETICHETTE_TIPO_CONTRATTO = {
  primo: "Primo contratto",
  aggiuntivo: "Contratto aggiuntivo",
  rinnovo: "Rinnovo",
  regolarizzazione: "Regolarizzazione",
};

const SPIEGAZIONI_TIPO_CONTRATTO = {
  primo: "Il contratto che ha creato il cliente alla firma.",
  aggiuntivo: "Servizi in più rispetto ai contratti in essere, con la loro attivazione e una scadenza propria.",
  rinnovo: "Sostituisce il contratto precedente dalla sua scadenza, senza una nuova attivazione.",
  regolarizzazione: "Primo contratto nell'app di un cliente che esisteva già: riprende i servizi della scheda, senza una nuova attivazione.",
};

// La regolarizzazione è un rinnovo per il backend (vedi regolarizzabile in
// routes/contratti.py), ma per chi legge la scheda non rinnova nulla.
const tipoVisibile = (c) => (c.regolarizzazione ? "regolarizzazione" : c.tipo);

function statoContratto(c) {
  if (c.stato !== "completato") {
    return { tono: "neutral", testo: "Bozza", spiegazione: "Wizard non ancora completato: riprendilo dalla pagina Contratti." };
  }
  if (c.stato_firma === "firmato") {
    return { tono: "success", testo: "Firmato", spiegazione: "Firmato dal cliente: servizi e pacchetti sono applicati alla scheda." };
  }
  if (c.stato_firma === "in_attesa") {
    return { tono: "warning", testo: "In attesa di firma", spiegazione: "Link di firma inviato: il contratto si applica al cliente quando lo firma." };
  }
  return { tono: "neutral", testo: "Da inviare", spiegazione: "Completato ma non ancora inviato al cliente per la firma." };
}

// Il cliente come contenitore di contratti: il primo lo ha creato alla firma,
// poi contratti aggiuntivi (servizi in più) e rinnovi (sostituiscono un
// contratto in essere). La creazione avviene nel wizard della pagina
// Contratti, a cui si passa il cliente (e il contratto da rinnovare) nell'URL.
function ContrattiClienteSection({ client, contratti, rinnovoPregressoId }) {
  const navigate = useNavigate();
  const pregresso = client?.contratto_pregresso || null;

  // "Rinnova" agisce sul contratto in essere: il primo contratto o il suo
  // ultimo rinnovo firmato, se non è già stato rinnovato. Per un cliente
  // precedente all'app senza contratti firmati, il contratto pregresso.
  const inEssere = [...contratti]
    .filter(c => c.tipo !== "aggiuntivo" && c.stato_firma === "firmato" && !c.rinnovato_da)
    .sort((a, b) => String(b.data_scadenza || "").localeCompare(String(a.data_scadenza || "")))[0];
  const haPrincipaleFirmato = contratti.some(c => c.tipo !== "aggiuntivo" && c.stato_firma === "firmato");
  const rinnovaPregresso = !haPrincipaleFirmato && !!pregresso && !rinnovoPregressoId;
  const clienteDaContratto = contratti.some(c => c.stato_firma === "firmato") || !!pregresso;
  // Cliente precedente all'app senza nessun contratto: il suo primo contratto
  // qui è una regolarizzazione, precompilata con i dati della scheda. Sparisce
  // appena ne esiste una (anche in bozza, da riprendere dalla lista sopra).
  const regolarizza = !client?.contratto_id && !pregresso && !rinnovoPregressoId && !haPrincipaleFirmato;

  if (contratti.length === 0 && !pregresso && !regolarizza) return null;

  const tile = ICON_TILES.viola;
  // Il wizard è quello della pagina dell'ambito: un rinnovo resta in quello
  // del contratto che rinnova (il backend rifiuta il cambio).
  const apri = (params, ambito = "standard") => navigate(`${AMBITI_CONTRATTO[ambito].percorso}?${new URLSearchParams(params)}`);
  const dataIt = (v) => (v ? new Date(v).toLocaleDateString("it-IT") : null);

  // Rinnovo: il contratto da rinnovare e la sua scadenza. Si apre solo
  // nell'ultimo mese (FINESTRA_RINNOVO_MESI): prima il pulsante resta visibile
  // ma inattivo, e il tooltip dice da quando si potrà usare.
  const scadenzaDaRinnovare = inEssere ? inEssere.data_scadenza : rinnovaPregresso ? pregresso.data_scadenza : null;
  const puoRinnovare = !!(inEssere || rinnovaPregresso);
  const rinnovoApribile = puoRinnovare && rinnovoAperto(scadenzaDaRinnovare);
  const apertura = aperturaRinnovo(scadenzaDaRinnovare);
  const descrizioneRinnovo = !puoRinnovare
    ? "Nessun contratto da rinnovare: il contratto in essere ha già un rinnovo, oppure non è ancora firmato."
    : rinnovoApribile
      ? `Crea il rinnovo ${inEssere ? `del ${ETICHETTE_TIPO_CONTRATTO[tipoVisibile(inEssere)].toLowerCase()}` : "del contratto in corso"}, in scadenza il ${dataIt(scadenzaDaRinnovare)}. Decorre dalla scadenza e non richiede una nuova attivazione; i servizi attuali partono già selezionati.`
      : `Si attiva dal ${dataIt(apertura)}, un mese prima della scadenza del ${dataIt(scadenzaDaRinnovare)}: prima di allora servizi e prezzi verrebbero fissati con troppo anticipo.`;

  return (
    <SectionCard
      icon={FileSignature}
      iconBg={tile.bg}
      iconColor={tile.color}
      title="Contratti"
      count={contratti.length || null}
      dense
      testId="contratti-cliente-section"
    >
      <div className="flex flex-col gap-2">
        {contratti.length === 0 ? (
          <EmptyState>
            {pregresso
              ? <>Nessun contratto firmato nell&apos;app: il contratto in corso è quello precedente.</>
              : <>Nessun contratto nell&apos;app per questo cliente.</>}
          </EmptyState>
        ) : contratti.map(c => {
          const stato = statoContratto(c);
          const inizio = dataIt(c.data_inizio);
          const fine = dataIt(c.data_scadenza);
          return (
            <div
              key={c.contratto_id}
              className="p-[11px_13px] rounded-[12px] border border-[#ededf4] bg-[#fafafc] hover:border-[#c9c9f0] transition-colors"
              data-testid={`cliente-contratto-${c.contratto_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <InfoTooltip title={ETICHETTE_TIPO_CONTRATTO[tipoVisibile(c)] || ETICHETTE_TIPO_CONTRATTO.primo} description={`${SPIEGAZIONI_TIPO_CONTRATTO[tipoVisibile(c)] || SPIEGAZIONI_TIPO_CONTRATTO.primo} Clicca per aprirne la scheda.`}>
                  <button
                    type="button"
                    onClick={() => navigate(`${percorsoContratto(c)}?contratto=${c.contratto_id}`)}
                    className="text-[13.5px] font-semibold text-[#15161c] hover:text-[#5b5bd6] transition-colors truncate text-left"
                  >
                    {ETICHETTE_TIPO_CONTRATTO[tipoVisibile(c)] || ETICHETTE_TIPO_CONTRATTO.primo}
                  </button>
                </InfoTooltip>
                <div className="flex items-center gap-1.5 shrink-0">
                  {c.documento_legale && (
                    <InfoTooltip
                      title="Documento da integrare"
                      description={`${c.documento_legale.mancano?.length === 2 ? "Mancano tipo e numero" : `Manca il ${c.documento_legale.mancano?.[0]}`} del documento del legale rappresentante. ${c.documento_legale.in_ritardo ? "Sono passati più di 15 giorni dalla firma: l'amministrazione è stata avvisata." : "Se dopo 15 giorni dalla firma manca ancora, viene avvisata l'amministrazione."}`}
                    >
                      <BadgeTono tone="warning" testId={`cliente-contratto-documento-${c.contratto_id}`}>Documento</BadgeTono>
                    </InfoTooltip>
                  )}
                  {c.rinnovato_da && (
                    <InfoTooltip title="Rinnovato" description="Questo contratto ha già un rinnovo: alla scadenza prosegue con quello. Non si può rinnovare una seconda volta.">
                      <BadgeTono>Rinnovato</BadgeTono>
                    </InfoTooltip>
                  )}
                  <InfoTooltip title={stato.testo} description={stato.spiegazione}>
                    <BadgeTono tone={stato.tono} dot>{stato.testo}</BadgeTono>
                  </InfoTooltip>
                </div>
              </div>
              {c.servizi?.length > 0 && (
                <div className="text-[12.5px] text-[#565d6d] mt-0.5 truncate">{c.servizi.join(" + ")}</div>
              )}
              {c.stato_firma === "firmato" && c.data_scadenza ? (
                <BarraScadenza
                  inizio={c.data_inizio}
                  scadenza={c.data_scadenza}
                  rinnovato={!!c.rinnovato_da}
                  testId={`cliente-contratto-scadenza-${c.contratto_id}`}
                />
              ) : inizio && fine ? (
                // Prima della firma le date sono indicative: nessuna barra.
                <div className="text-[12px] text-[#9095a1] mt-0.5 scheda-font-mono">{inizio} → {fine}</div>
              ) : null}
            </div>
          );
        })}
      </div>
      {regolarizza && (
        <div className="flex gap-2 mt-3 flex-wrap">
          <InfoTooltip
            title="Crea contratto"
            description="Prepara il primo contratto nell'app per questo cliente: anagrafica presa dalla scheda e servizi attuali già selezionati. Decorre da oggi e non richiede una nuova attivazione; alla firma i servizi del contratto sostituiscono quelli della scheda."
          >
            <button
              className={PRIMARY_BTN}
              onClick={() => apri({ nuovo: "rinnovo", cliente: client.client_id })}
              data-testid="cliente-contratto-regolarizza-btn"
            >
              <Plus className="w-3 h-3" /> Crea contratto
            </button>
          </InfoTooltip>
        </div>
      )}
      {clienteDaContratto && (
        <div className="flex gap-2 mt-3 flex-wrap">
          <InfoTooltip
            title="Contratto aggiuntivo"
            description="Vende servizi in più al cliente, accanto a quelli in essere. Ogni servizio avrà la sua attivazione nei Pagamenti e il contratto una scadenza propria. I servizi già attivi non sono selezionabili."
          >
            <button
              className={SECONDARY_BTN}
              onClick={() => apri({ nuovo: "aggiuntivo", cliente: client.client_id })}
              data-testid="cliente-contratto-aggiuntivo-btn"
            >
              <Plus className="w-3 h-3" /> Contratto aggiuntivo
            </button>
          </InfoTooltip>
          <InfoTooltip
            title="Contratto aggiuntivo eventi"
            description="Come il contratto aggiuntivo, ma con i servizi del Catalogo Servizi Eventi. Si gestisce dalla pagina Contratti eventi."
          >
            <button
              className={SECONDARY_BTN}
              onClick={() => apri({ nuovo: "aggiuntivo", cliente: client.client_id }, "eventi")}
              data-testid="cliente-contratto-aggiuntivo-eventi-btn"
            >
              <Plus className="w-3 h-3" /> Contratto eventi
            </button>
          </InfoTooltip>
          {puoRinnovare && (
            <InfoTooltip title={rinnovoApribile ? "Rinnova" : "Rinnovo non ancora disponibile"} description={descrizioneRinnovo}>
              <button
                className={`${SECONDARY_BTN} ${rinnovoApribile ? "" : "opacity-50 cursor-not-allowed hover:border-[#e0e0ea] hover:text-[#565d6d]"}`}
                // aria-disabled e non disabled: un pulsante disabled non riceve
                // il passaggio del mouse e il tooltip col motivo non si aprirebbe.
                aria-disabled={!rinnovoApribile}
                onClick={() => {
                  if (!rinnovoApribile) return;
                  apri(inEssere
                    ? { nuovo: "rinnovo", cliente: client.client_id, rinnova: inEssere.contratto_id }
                    : { nuovo: "rinnovo", cliente: client.client_id }, ambitoDi(inEssere));
                }}
                data-testid="cliente-contratto-rinnova-btn"
              >
                <RefreshCw className="w-3 h-3" /> Rinnova
                {!rinnovoApribile && apertura && <span className="font-medium">· dal {dataIt(apertura)}</span>}
              </button>
            </InfoTooltip>
          )}
        </div>
      )}
    </SectionCard>
  );
}

function teamInAttesaAttivazione(client) {
  return !!client?.contratto_id
    && Number(client?.pacchetto_importo_attivazione || 0) > 0
    && !client?.attivazione_pagata;
}

// Il link al contratto è nuovo: resta evidenziato con la pecetta "News" per
// 60 giorni dal rilascio (23/09/2026), poi torna un chip come gli altri.
const NOVITA_CONTRATTO_FINO_AL = new Date("2026-11-22T23:59:59");

const MSG_TEAM_BLOCCATO = "Il team si assegna dopo la registrazione del pagamento di attivazione";

// Stessi ruoli di require_pm_or_amm, che protegge PATCH /clients/{id}/commerciale.
function puoAssegnareCommerciale(user) {
  if (user?.email === "info@diecixcento.com" || user?.is_responsabile_operativo) return true;
  const ruoli = [user?.role, ...(user?.secondary_roles || [])].filter(Boolean);
  return ruoli.some(r => ["pm", "amm", "direttore_tecnico", "direttore_commerciale"].includes(r));
}

// Chi ha venduto il contratto (backend/routes/commerciale_cliente.py). Se il
// cliente è nato da un contratto è chi l'ha creato e non si cambia da qui; per
// gli altri clienti si sceglie dalla lista dei commerciali.
function CommercialeSection({ client, clientId, user, onUpdate }) {
  const commerciale = client?.commerciale || null;
  const daContratto = commerciale?.origine === "contratto";
  const modificabile = !daContratto && puoAssegnareCommerciale(user);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [commerciali, setCommerciali] = useState([]);
  const [scelto, setScelto] = useState("unassigned");

  const openEdit = async () => {
    setEditing(true);
    setScelto(commerciale?.user_id || "unassigned");
    setLoadingUsers(true);
    try {
      const res = await apiFetch("/users/commerciale");
      if (res.ok) setCommerciali(await res.json());
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/commerciale`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ commerciale_id: scelto === "unassigned" ? null : scelto }),
      });
      if (res.ok) {
        toast.success("Commerciale aggiornato");
        setEditing(false);
        onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } finally {
      setSaving(false);
    }
  };

  // Un commerciale manuale non più in elenco (disattivato o senza più il ruolo)
  // resta selezionabile, altrimenti la tendina lo mostrerebbe vuoto.
  const opzioni = commerciale && !daContratto && !commerciali.some(u => u.user_id === commerciale.user_id)
    ? [...commerciali, { user_id: commerciale.user_id, name: commerciale.name, picture: commerciale.picture }]
    : commerciali;
  const itemCls = "rounded-[10px] py-1.5 pl-2 pr-8 my-0.5 cursor-pointer focus:bg-[#f6f6fb] data-[state=checked]:bg-[#eeeefc]";

  return (
    <SectionCard
      icon={User}
      iconBg={ICON_TILES.viola.bg}
      iconColor={ICON_TILES.viola.color}
      title="Commerciale"
      dense
      testId="commerciale-section"
      action={
        <span className="inline-flex items-center gap-2">
          <InfoHint
            title="Commerciale"
            description={daContratto
              ? "Chi ha creato il contratto da cui è nato il cliente. Per cambiarlo si interviene sul contratto, non dalla scheda."
              : "Il cliente non è nato da un contratto nell'app: indica a mano chi l'ha venduto. Riceve gli alert sui pagamenti in ritardo."}
            testId="commerciale-info"
          />
          {modificabile && !editing && (
            <button className={SECONDARY_BTN} onClick={openEdit} data-testid="edit-commerciale-btn">
              {commerciale ? <><Pencil className="w-3 h-3" /> Modifica</> : <><Plus className="w-3 h-3" /> Indica</>}
            </button>
          )}
        </span>
      }
    >
      {editing ? (
        <div className="flex flex-col gap-3">
          <Select value={scelto} onValueChange={setScelto} disabled={loadingUsers}>
            <SelectTrigger className="h-9 rounded-[10px] border border-[#e4e4ed] bg-white text-[13px] shadow-none focus:ring-0 hover:border-[#c9c9f0] transition-colors" data-testid="commerciale-select">
              <SelectValue placeholder="Nessuno" />
            </SelectTrigger>
            <SelectContent className="rounded-[14px] border-[#e9e9f1] shadow-lg p-1.5 scheda-font-ui">
              <SelectItem value="unassigned" className={itemCls}>
                <span className="text-[13.5px] text-[#9095a1]">Nessuno</span>
              </SelectItem>
              {opzioni.map(u => (
                <SelectItem key={u.user_id} value={u.user_id} className={itemCls}>
                  <span className="flex items-center gap-2.5">
                    <UserAvatar name={u.name} picture={u.picture} size={24} />
                    <span className="text-[13.5px] text-[#15161c]">{u.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!loadingUsers && commerciali.length === 0 && (
            <p className="text-[12.5px] text-[#9095a1]">Nessun utente ha il ruolo Commerciale: si assegna dalla pagina Team.</p>
          )}
          <div className="flex gap-2 justify-end">
            <button className={GHOST_BTN} onClick={() => setEditing(false)} disabled={saving}>Annulla</button>
            <button className={PRIMARY_BTN} onClick={handleSave} disabled={saving || loadingUsers} data-testid="save-commerciale-btn">
              {saving ? "Salvo..." : "Salva"}
            </button>
          </div>
        </div>
      ) : commerciale ? (
        <div className="flex items-center gap-3" data-testid="commerciale-cliente">
          <UserAvatar name={commerciale.name} picture={commerciale.picture} size={36} />
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-[#15161c] truncate">{commerciale.name}</div>
            <div className="text-[12px] text-[#9095a1]">{daContratto ? "Ha creato il contratto" : "Indicato a mano"}</div>
          </div>
        </div>
      ) : (
        <EmptyState>Nessun commerciale indicato per questo cliente.</EmptyState>
      )}
    </SectionCard>
  );
}

function TeamSection({ client, team, clientId, onUpdate, openSignal, innerRef }) {
  const bloccato = teamInAttesaAttivazione(client);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  // Finestra di ~1,8s dopo il salvataggio in cui le righe fanno l'animazione di
  // conferma (classi team-salvato-* in index.css).
  const [appenaSalvato, setAppenaSalvato] = useState(false);

  useEffect(() => {
    if (!appenaSalvato) return undefined;
    const t = setTimeout(() => setAppenaSalvato(false), 1800);
    return () => clearTimeout(t);
  }, [appenaSalvato]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [pmUsers, setPmUsers] = useState([]);
  const [smmUsers, setSmmUsers] = useState([]);
  const [vmUsers, setVmUsers] = useState([]);
  const [editorUsers, setEditorUsers] = useState([]);
  const [filmmakerUsers, setFilmmakerUsers] = useState([]);
  const [form, setForm] = useState({ pm: "unassigned", smm: "unassigned", vm: "unassigned", editor: "unassigned", filmmaker: "unassigned" });

  const rows = [
    { key: "project", label: "Project" },
    { key: "social_media", label: "Social Media" },
    { key: "content_creator", label: "Content Creator" },
    { key: "editor", label: "Editor" },
    { key: "videomaker", label: "Videomaker" },
  ].map(r => ({ ...r, name: team?.[r.key], picture: team?.[`${r.key}_picture`] })).filter(r => r.name);

  const openEdit = async () => {
    setEditing(true);
    setLoadingUsers(true);
    try {
      // "/users/videomaker" sono i Content Creator (chiave ereditata); i Videomaker
      // veri e propri hanno ruolo "filmmaker".
      const [pmRes, smmRes, vmRes, edRes, fmRes] = await Promise.all([
        apiFetch("/users/pm"), apiFetch("/users/smm"), apiFetch("/users/videomaker"), apiFetch("/users/editor"),
        apiFetch("/users/filmmaker"),
      ]);
      if (pmRes.ok) setPmUsers(await pmRes.json());
      if (smmRes.ok) setSmmUsers(await smmRes.json());
      if (vmRes.ok) setVmUsers(await vmRes.json());
      if (edRes.ok) setEditorUsers(await edRes.json());
      if (fmRes.ok) setFilmmakerUsers(await fmRes.json());
    } finally {
      setLoadingUsers(false);
    }
    setForm({
      pm: client?.assigned_pm_id || "unassigned",
      smm: client?.smm_tiktok_id || client?.smm_social_id || client?.assigned_smm_id || "unassigned",
      vm: client?.videomaker_id || "unassigned",
      editor: client?.editor_id || "unassigned",
      filmmaker: client?.filmmaker_id || "unassigned",
    });
  };

  useEffect(() => {
    if (openSignal > 0) {
      if (!bloccato) openEdit();
      innerRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        assigned_pm_id: form.pm === "unassigned" ? null : form.pm,
        smm_tiktok_id: form.smm === "unassigned" ? null : form.smm,
        videomaker_id: form.vm === "unassigned" ? null : form.vm,
        editor_id: form.editor === "unassigned" ? null : form.editor,
        filmmaker_id: form.filmmaker === "unassigned" ? null : form.filmmaker,
      };
      const res = await apiFetch(`/clients/${clientId}/assign-reparto-staff`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Team aggiornato");
        setAppenaSalvato(true);
        setEditing(false);
        onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nell'assegnazione");
      }
    } finally {
      setSaving(false);
    }
  };

  const selectCls = "h-9 rounded-[10px] border border-[#e4e4ed] bg-white text-[13px] shadow-none focus:ring-0";

  return (
    <SectionCard
      icon={Users}
      iconBg="#f1f1f8"
      iconColor="#565d6d"
      title="Team assegnato"
      dense
      testId="team-section"
      innerRef={innerRef}
      action={appenaSalvato ? (
        <span className="team-salvato-badge inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-full bg-[#e7f7ee] text-[#0f7040] text-[12px] font-semibold" data-testid="team-salvato-badge">
          <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.2} /> Team salvato
        </span>
      ) : !editing && rows.length > 0 ? (
        <button className={SECONDARY_BTN} onClick={openEdit} disabled={bloccato} title={bloccato ? MSG_TEAM_BLOCCATO : undefined} data-testid="edit-team-btn">
          <Pencil className="w-3 h-3" /> Modifica
        </button>
      ) : null}
    >
      {editing ? (
        <div className="space-y-3">
          {[
            { key: "pm", label: "Project", list: pmUsers },
            { key: "smm", label: "Social Media", list: smmUsers },
            { key: "vm", label: "Content Creator", list: vmUsers },
            { key: "editor", label: "Editor", list: editorUsers },
            // Il Videomaker non c'è su tutti i clienti: la X lo toglie senza dover
            // aprire la tendina e cercare "Nessuno".
            { key: "filmmaker", label: "Videomaker", list: filmmakerUsers, optional: true },
          ].map(f => (
            <div key={f.key}>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-[#9095a1]">
                {f.label}
                {f.optional && <span className="normal-case tracking-normal font-medium text-[#b3b7c2]"> · opzionale</span>}
              </label>
              <div className="flex items-center gap-2 mt-1">
              <Select value={form[f.key]} onValueChange={(v) => setForm(prev => ({ ...prev, [f.key]: v }))} disabled={loadingUsers}>
                <SelectTrigger className={`${selectCls} flex-1 hover:border-[#c9c9f0] transition-colors`}>
                  <SelectValue placeholder="Nessuno" />
                </SelectTrigger>
                <SelectContent className="rounded-[14px] border-[#e9e9f1] shadow-lg p-1.5 scheda-font-ui">
                  <SelectItem
                    value="unassigned"
                    className="rounded-[10px] py-1.5 pl-2 pr-8 my-0.5 cursor-pointer focus:bg-[#f6f6fb] data-[state=checked]:bg-[#f6f6fb]"
                  >
                    <span className="flex items-center gap-2.5 text-[#9095a1]">
                      <span className="w-6 h-6 rounded-full border border-dashed border-[#dcdce8] flex items-center justify-center shrink-0">
                        <User className="w-3.5 h-3.5 text-[#c3c6d0]" strokeWidth={2} />
                      </span>
                      <span className="text-[13.5px]">Nessuno</span>
                    </span>
                  </SelectItem>
                  {f.list.map(u => (
                    <SelectItem
                      key={u.user_id}
                      value={u.user_id}
                      className="rounded-[10px] py-1.5 pl-2 pr-8 my-0.5 cursor-pointer focus:bg-[#f6f6fb] data-[state=checked]:bg-[#eeeefc]"
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="w-6 h-6 rounded-full bg-[#eeeefc] flex items-center justify-center shrink-0">
                          <User className="w-3.5 h-3.5 text-[#5b5bd6]" strokeWidth={2} />
                        </span>
                        <span className="text-[13.5px] text-[#15161c]">{u.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {f.optional && form[f.key] !== "unassigned" && (
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, [f.key]: "unassigned" }))}
                  disabled={loadingUsers}
                  title={`Rimuovi ${f.label}`}
                  aria-label={`Rimuovi ${f.label}`}
                  className="w-9 h-9 shrink-0 rounded-[10px] border border-[#e0e0ea] bg-white text-[#9095a1] hover:border-[#f3c9c3] hover:bg-[#fdecea] hover:text-[#c0392b] transition-colors inline-flex items-center justify-center disabled:opacity-50"
                  data-testid={`remove-${f.key}-btn`}
                >
                  <X className="w-4 h-4" strokeWidth={2.2} />
                </button>
              )}
              </div>
            </div>
          ))}
          <div className="flex gap-2 justify-end pt-1">
            <button className={GHOST_BTN} onClick={() => setEditing(false)} disabled={saving}>Annulla</button>
            <button className={PRIMARY_BTN} onClick={handleSave} disabled={saving} data-testid="save-team-btn">
              {saving ? "Salvo..." : "Salva"}
            </button>
          </div>
        </div>
      ) : rows.length > 0 ? (
        <div className="flex flex-col gap-3">
          {rows.map((r, i) => (
            <div
              key={r.key}
              className={`flex items-center gap-3.5 px-4 py-3 rounded-2xl bg-white border border-[#e9e9f2] shadow-[0_3px_10px_rgba(15,23,42,0.06),0_1px_3px_rgba(15,23,42,0.04)] hover:shadow-[0_6px_16px_rgba(15,23,42,0.09)] hover:-translate-y-0.5 transition-all duration-200 ${appenaSalvato ? "team-salvato-riga" : "scheda-pop-anim"}`}
              style={appenaSalvato ? { animationDelay: `${i * 90}ms` } : undefined}
            >
              <div className="w-9 h-9 rounded-full bg-[#eeeefc] border border-[#dedeff] flex items-center justify-center shrink-0 shadow-xs overflow-hidden">
                {r.picture ? (
                  <img src={r.picture} alt={r.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[13.5px] font-bold text-[#5b5bd6]">{r.name.charAt(0)}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold text-[#15161c] truncate">{r.name}</p>
                <p className="text-[12.5px] font-medium text-[#7e8494]">{r.label}</p>
              </div>
              {appenaSalvato && (
                <span
                  className="team-salvato-spunta w-6 h-6 rounded-full bg-[#e7f7ee] flex items-center justify-center shrink-0"
                  style={{ animationDelay: `${i * 90 + 280}ms` }}
                >
                  <Check className="w-3.5 h-3.5 text-[#0f7040]" strokeWidth={2.6} />
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="p-4 rounded-[13px] border border-dashed border-[#dcdce8] bg-[#fbfbfd]">
            <p className="text-[13.5px] leading-[1.5] text-[#6b7280]">
              {bloccato
                ? "In attesa del pagamento di attivazione: il team si potrà assegnare quando l'amministrazione lo registra nei Pagamenti."
                : "Nessun team assegnato. Il cliente non comparirà nei carichi di lavoro."}
            </p>
          </div>
          <button
            className={`${PRIMARY_BTN} w-full`}
            onClick={openEdit}
            disabled={bloccato}
            title={bloccato ? MSG_TEAM_BLOCCATO : undefined}
            data-testid="assign-team-btn"
          >
            Assegna team
          </button>
        </div>
      )}
    </SectionCard>
  );
}

const parseImportoIt = (raw) => {
  if (raw === "" || raw == null) return NaN;
  return Number(String(raw).trim().replace(/\./g, "").replace(",", "."));
};

const nomeMeseIt = (ym) => new Date(ym + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" });

// Fatturato generato al cliente mese per mese, per i servizi con una quota
// mensile a percentuale (es. TikTok Shop). Il fatturato del mese M arriva a
// inizio M+1 e la sua percentuale entra nel dovuto di M+1, accanto al canone
// fisso (backend/routes/fatturato_percentuale.py).
function FatturatoPercentualeSection({ clientId, versione, onUpdate }) {
  const [dati, setDati] = useState(null);
  const [mese, setMese] = useState("");
  const [importo, setImporto] = useState("");
  const [saving, setSaving] = useState(false);
  const [daEliminare, setDaEliminare] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const carica = async () => {
    const res = await apiFetch(`/clients/${clientId}/fatturato-percentuale`);
    if (!res.ok) return;
    const d = await res.json();
    setDati(d);
    // Il form parte dal mese appena chiuso, se manca: è il dato che si aspetta.
    setMese(d.mese_da_inserire_mancante ? d.mese_da_inserire : "");
    setImporto("");
  };

  useEffect(() => { setDati(null); carica(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Dopo uno stepper dei Pagamenti si rilegge senza svuotare: niente salto.
  useEffect(() => { if (versione) carica(); }, [versione]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!dati || (!dati.attivo && dati.voci.length === 0)) return null;

  const voci = dati.voci;
  const pct = dati.pct || 0;
  const meseSuccessivo = (ym) => {
    const [a, m] = ym.split("-").map(Number);
    return m === 12 ? `${a + 1}-01` : `${a}-${String(m + 1).padStart(2, "0")}`;
  };
  const vocePerMese = Object.fromEntries(voci.map(v => [v.mese, v]));
  const quotaCorrente = voci.find(v => v.mese_dovuto === dati.mese_corrente)?.quota;
  const importoNum = importo === "" ? NaN : Number(importo);
  const puoSalvare = !!mese && !Number.isNaN(importoNum) && importoNum >= 0 && mese <= dati.mese_corrente;
  // Un mese già inserito con una percentuale scelta dallo stepper la mantiene.
  const pctMese = (mese && vocePerMese[mese]?.pct_personalizzata && vocePerMese[mese].pct != null) ? vocePerMese[mese].pct : pct;
  const anteprima = puoSalvare && pctMese > 0 ? Math.round(importoNum * pctMese) / 100 : null;

  const salva = async () => {
    if (!puoSalvare) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/fatturato-percentuale/${mese}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ importo: importoNum }),
      });
      if (res.ok) {
        toast.success(`Fatturato di ${nomeMeseIt(mese)} salvato`);
        await carica();
        onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } finally {
      setSaving(false);
    }
  };

  const elimina = async () => {
    if (!daEliminare) return;
    setEliminando(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/fatturato-percentuale/${daEliminare.mese}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Voce eliminata");
        setDaEliminare(null);
        await carica();
        onUpdate();
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } finally {
      setEliminando(false);
    }
  };

  return (
    <SectionCard
      icon={Percent}
      iconBg={ICON_TILES.rosa.bg}
      iconColor={ICON_TILES.rosa.color}
      title={`Fatturato percentuale${dati.servizio_nome ? ` — ${dati.servizio_nome}` : ""}`}
      testId="fatturato-percentuale-section"
      action={
        <StatBlock
          label={`Quota di ${nomeMeseIt(dati.mese_corrente).split(" ")[0]}`}
          value={quotaCorrente != null ? formatCurrency(quotaCorrente, { decimals: 0 }) : "—"}
          color={quotaCorrente != null ? "#0f7040" : "#c3c6d0"}
        />
      }
    >
      <div className="flex items-start gap-1.5 text-[13px] leading-[1.55] text-[#565d6d]">
        <p>
          A inizio mese inserisci il fatturato del cliente del mese appena chiuso: il {formatNumber(pct)}% si somma
          al canone fisso del mese in corso, nel dovuto e nei compensi.
        </p>
        <InfoHint
          title="Perché il mese dopo"
          description="Il fatturato di un mese si conosce solo a mese chiuso. La quota entra quindi nel dovuto del mese successivo: il fatturato di agosto, inserito a settembre, si paga con il canone di settembre."
        />
      </div>

      {dati.attivo && dati.mese_da_inserire_mancante && (
        <div className="mt-3 flex items-center gap-2 rounded-[11px] border border-[#ecd9ae] bg-[#fffaf0] px-3.5 py-2.5 text-[13px] text-[#7a4e07]" data-testid="fatturato-percentuale-mancante">
          <AlertTriangle className="w-4 h-4 shrink-0 text-[#b07314]" strokeWidth={1.9} />
          <span>
            Manca il fatturato di <b>{nomeMeseIt(dati.mese_da_inserire)}</b>: la quota di {nomeMeseIt(dati.mese_corrente)} non è ancora nel dovuto.
          </span>
        </div>
      )}

      {dati.puo_modificare && (
        <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end mt-4">
          <div className="min-w-0">
            <label className="text-xs text-muted-foreground">Mese del fatturato</label>
            <MonthPicker
              value={mese}
              onChange={(m) => { setMese(m); setImporto(vocePerMese[m] ? String(vocePerMese[m].importo) : ""); }}
              className={`${FIELD_CLS} h-[38px] mt-1`}
              data-testid="fatturato-percentuale-mese"
            />
          </div>
          <div className="min-w-0">
            <label className="text-xs text-muted-foreground">Fatturato del cliente</label>
            <div className="mt-1">
              <NumberField
                value={importo}
                onChange={setImporto}
                onKeyDown={(e) => { if (e.key === "Enter") salva(); }}
                placeholder="0"
                testId="fatturato-percentuale-importo"
              />
            </div>
          </div>
          <button onClick={salva} disabled={saving || !puoSalvare} className={`${PRIMARY_BTN} h-[38px] px-5 col-span-2 sm:col-span-1 justify-center`} data-testid="fatturato-percentuale-salva">
            {mese && vocePerMese[mese] ? "Aggiorna" : <><Plus className="w-4 h-4" /> Aggiungi</>}
          </button>
          {mese && (
            <p className="col-span-2 sm:col-span-3 text-[12.5px] text-[#6b7280]" data-testid="fatturato-percentuale-anteprima">
              {mese > dati.mese_corrente
                ? "Il fatturato di un mese futuro non si può ancora inserire."
                : <>Entra nel dovuto di <b className="text-[#3c414d]">{nomeMeseIt(meseSuccessivo(mese))}</b>
                  {anteprima != null && <>: {formatNumber(pctMese)}% = <b className="text-[#0f7040] scheda-font-mono">{formatCurrency(anteprima, { decimals: 0 })}</b></>}
                  {vocePerMese[mese] && <> · sostituisce i {formatCurrency(vocePerMese[mese].importo, { decimals: 0 })} inseriti</>}</>}
            </p>
          )}
        </div>
      )}

      {voci.length > 0 ? (
        <div className="mt-4 rounded-[12px] border border-[#ededf4] divide-y divide-[#f0f0f6]">
          {[...voci].reverse().map(v => (
            <div key={v.mese} className="flex items-center gap-3 px-3.5 py-2.5 text-[13.5px]" data-testid={`fatturato-percentuale-voce-${v.mese}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#15161c] capitalize">{v.nome_mese}</div>
                <div className="text-[12px] text-[#9095a1]">
                  {v.quota != null
                    ? <>{formatNumber(v.pct)}%{v.pct_personalizzata ? ` (contratto ${formatNumber(pct)}%)` : ""} nel dovuto di {v.nome_mese_dovuto}</>
                    : <>Nessuna quota a {v.nome_mese_dovuto}: servizio non attivo in quel mese</>}
                </div>
              </div>
              <div className="text-right">
                <div className="scheda-font-mono font-semibold text-[#15161c]">{formatCurrency(v.importo, { decimals: 0 })}</div>
                {v.quota != null && <div className="scheda-font-mono text-[12px] text-[#0f7040]">+ {formatCurrency(v.quota, { decimals: 0 })}</div>}
              </div>
              {dati.puo_modificare && (
                <div className="flex items-center gap-1">
                  <button
                    className="h-7 w-7 rounded-[8px] flex items-center justify-center text-[#9095a1] hover:text-[#5b5bd6] hover:bg-[#f1f1f8] transition-colors"
                    onClick={() => { setMese(v.mese); setImporto(String(v.importo)); }}
                    aria-label={`Modifica ${v.nome_mese}`}
                    data-testid={`fatturato-percentuale-modifica-${v.mese}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    className="h-7 w-7 rounded-[8px] flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    onClick={() => setDaEliminare(v)}
                    aria-label={`Elimina ${v.nome_mese}`}
                    data-testid={`fatturato-percentuale-elimina-${v.mese}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4">
          <EmptyState boxed>Nessun fatturato inserito</EmptyState>
        </div>
      )}

      <AlertDialog open={!!daEliminare} onOpenChange={(open) => { if (!open && !eliminando) setDaEliminare(null); }}>
        <AlertDialogContent className={DIALOG_CONTENT_CLS} data-testid="fatturato-percentuale-elimina-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#15161c]">Eliminare il fatturato di {daEliminare?.nome_mese}?</AlertDialogTitle>
            <AlertDialogDescription>
              La quota esce dal dovuto di {daEliminare?.nome_mese_dovuto}. I pagamenti già registrati restano come sono.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminando}>Annulla</AlertDialogCancel>
            <button type="button" onClick={elimina} disabled={eliminando} className={PRIMARY_BTN} data-testid="fatturato-percentuale-elimina-conferma">
              {eliminando ? "Eliminazione…" : "Elimina"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}

function RevenueSection({ clientId, revenue, onUpdate }) {
  const [newMonth, setNewMonth] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [adding, setAdding] = useState(false);
  const [monthToDelete, setMonthToDelete] = useState(null); // la riga {month, amount} in attesa di conferma
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deletingMonths, setDeletingMonths] = useState([]); // mesi in uscita: la barra sta rimpicciolendo
  const deleteTimers = useRef([]);

  useEffect(() => () => deleteTimers.current.forEach(window.clearTimeout), []);

  const parsedAmount = parseImportoIt(newAmount);
  const canAdd = !!newMonth && newAmount !== "" && !isNaN(parsedAmount) && parsedAmount >= 0;

  const handleAdd = async () => {
    if (!canAdd) {
      toast.error("Inserisci mese e importo");
      return;
    }
    setAdding(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/revenue`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ month: newMonth, amount: parsedAmount })
      });
      if (res.ok) {
        toast.success("Fatturato aggiunto");
        setNewMonth("");
        setNewAmount("");
        onUpdate();
      }
    } catch (error) {
      toast.error("Errore");
    } finally {
      setAdding(false);
    }
  };

  // La cancellazione è speculare all'aggiunta: la barra si rimpicciolisce verso il
  // basso (stesso movimento della crescita all'ingresso, invertito) prima che la
  // voce sparisca davvero dai dati — stesso pattern già in uso per le note.
  const handleDelete = async () => {
    if (!monthToDelete || deleteSubmitting) return;
    const month = monthToDelete.month;
    setDeleteSubmitting(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/revenue/${month}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Impossibile eliminare la voce");
      setDeletingMonths(prev => [...prev, month]);
      setMonthToDelete(null);
      toast.success("Voce eliminata");
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const timer = window.setTimeout(() => {
        onUpdate();
        setDeletingMonths(prev => prev.filter(m => m !== month));
        deleteTimers.current = deleteTimers.current.filter(id => id !== timer);
      }, reduceMotion ? 0 : 380);
      deleteTimers.current.push(timer);
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const maxAmount = Math.max(...(revenue.map(r => r.amount) || [0]), 1);
  const barPx = (amt) => Math.min(96, Math.max(14, (amt / maxAmount) * 96));
  const total = revenue.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <SectionCard
      icon={BarChart3}
      iconBg="#e7f7ee"
      iconColor="#0f7040"
      title="Fatturato cliente"
      testId="revenue-section"
      action={<StatBlock label="Totale" value={revenue.length > 0 ? formatCurrency(total, { decimals: 0 }) : "—"} color={revenue.length > 0 ? "#0f7040" : "#c3c6d0"} />}
    >
      <div
        className="rounded-[14px] flex items-end gap-2.5 p-[18px_18px_14px]"
        style={{
          border: `1px dashed ${revenue.length > 0 ? "#e9e9f1" : "#dcdce8"}`,
          background: "#fbfbfd",
          minHeight: 150,
        }}
      >
        {revenue.length > 0 ? (
          revenue.map((r, i) => {
            const prevAmount = i > 0 ? revenue[i - 1].amount : r.amount;
            const trend = r.amount >= prevAmount ? "linear-gradient(180deg,#5b5bd6,#7c7ce6)" : "linear-gradient(180deg,#d6465b,#e67c8c)";
            const isLeaving = deletingMonths.includes(r.month);
            return (
              <div key={r.month} className={`flex-1 max-w-[84px] flex flex-col items-center gap-1 ${isLeaving ? "scheda-row-out" : "scheda-row-anim"}`}>
                <span className="text-[11.5px] font-medium scheda-font-mono text-[#15161c]">{formatCurrency(r.amount, { decimals: 0 })}</span>
                <div
                  className={`w-full transition-[height] ${isLeaving ? "scheda-bar-shrink" : "scheda-bar-grow"}`}
                  style={{ height: `${barPx(r.amount)}px`, background: trend, borderRadius: "8px 8px 3px 3px", transitionDuration: "400ms" }}
                />
                <span className="text-[11.5px] font-medium text-[#6b7280]">
                  {new Date(r.month + "-01").toLocaleDateString("it-IT", { month: "short", year: "2-digit" })}
                </span>
              </div>
            );
          })
        ) : (
          <div className="w-full flex flex-col items-center justify-center gap-1 py-4">
            <TrendingUp className="w-[26px] h-[26px] text-[#c3c6d0]" strokeWidth={1.7} />
            <p className="text-[13.5px] font-medium text-[#9095a1]">Nessun dato fatturato inserito</p>
            <p className="text-[12.5px] text-[#b2b6c1]">Aggiungi il primo mese qui sotto</p>
          </div>
        )}
      </div>

      {revenue.length >= 2 && (
        <div className="mt-3 flex items-center gap-2">
          <TrendingUp className={`w-4 h-4 ${revenue[revenue.length - 1].amount >= revenue[revenue.length - 2].amount ? "text-[#0f7040]" : "text-red-500 rotate-180"}`} />
          <span className={`text-sm font-medium ${revenue[revenue.length - 1].amount >= revenue[revenue.length - 2].amount ? "text-[#0f7040]" : "text-red-500"}`}>
            {revenue[revenue.length - 1].amount >= revenue[revenue.length - 2].amount ? "In crescita" : "In calo"} rispetto al mese precedente
          </span>
        </div>
      )}

      {/* Su mobile il pulsante va a capo a tutta larghezza: in tre colonne
          il campo importo usciva dalla card. */}
      <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end pt-3 mt-4 border-t border-[#f0f0f6]">
        <div className="min-w-0">
          <label className="text-xs text-muted-foreground">Mese</label>
          <MonthPicker
            value={newMonth}
            onChange={setNewMonth}
            className={`${FIELD_CLS} h-11 mt-1`}
            data-testid="revenue-month-input"
          />
        </div>
        <div className="min-w-0">
          <label className="text-xs text-muted-foreground">Importo</label>
          <div className={`${FIELD_CLS} h-11 mt-1 flex items-center gap-1.5`}>
            <span className="text-[#9095a1]">€</span>
            <input
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
              placeholder="0,00"
              inputMode="decimal"
              className="flex-1 min-w-0 bg-transparent outline-none scheda-font-mono"
              data-testid="revenue-amount-input"
            />
          </div>
        </div>
        <button onClick={handleAdd} disabled={adding || !canAdd} className={`${PRIMARY_BTN} h-11 px-5 col-span-2 sm:col-span-1 justify-center`} data-testid="add-revenue-btn">
          <Plus className="w-4 h-4" /> Aggiungi
        </button>
      </div>

      {revenue.length > 0 && (
        <div className="mt-4 space-y-1 max-h-40 overflow-y-auto">
          {[...revenue].reverse().map(r => {
            const isLeaving = deletingMonths.includes(r.month);
            return (
              <div key={r.month} className={`flex items-center justify-between px-3 py-2 rounded-[10px] hover:bg-[#fbfbfd] text-sm transition-opacity duration-300 ${isLeaving ? "opacity-0" : "opacity-100"}`}>
                <span className="text-muted-foreground">
                  {new Date(r.month + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
                </span>
                <div className="flex items-center gap-2">
                  <span className="font-semibold scheda-font-mono">{formatCurrency(r.amount, { decimals: 0 })}</span>
                  <button
                    className="h-6 w-6 rounded-[8px] flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                    onClick={() => setMonthToDelete(r)}
                    disabled={isLeaving}
                    data-testid={`delete-revenue-${r.month}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!monthToDelete} onOpenChange={(open) => { if (!open && !deleteSubmitting) setMonthToDelete(null); }}>
        <AlertDialogContent className="scheda-font-ui max-w-md rounded-[18px] border-[#e9e9f1] duration-300 motion-reduce:animate-none" data-testid="delete-revenue-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#15161c]">Eliminare questa voce di fatturato?</AlertDialogTitle>
            <AlertDialogDescription>
              La voce verrà eliminata definitivamente. Questa operazione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {monthToDelete && (
            <p className="rounded-[10px] bg-[#f7f7fa] px-3 py-2 text-[13px] text-[#565d6d] flex items-center justify-between">
              <span>{new Date(monthToDelete.month + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</span>
              <span className="font-semibold scheda-font-mono text-[#15161c]">{formatCurrency(monthToDelete.amount, { decimals: 0 })}</span>
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>Annulla</AlertDialogCancel>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteSubmitting}
              className="inline-flex h-9 items-center justify-center rounded-[10px] bg-red-600 px-4 text-[13px] font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-50"
              data-testid="confirm-delete-revenue"
            >
              {deleteSubmitting ? "Eliminazione…" : "Elimina voce"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}

function NotesSection({ clientId, notes, user, team, clientName, onUpdate }) {
  const [newNote, setNewNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [searchParams] = useSearchParams();
  const highlightNoteId = searchParams.get("note");
  const highlightRef = useRef(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [replyToNote, setReplyToNote] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deletingNoteIds, setDeletingNoteIds] = useState([]);
  const deleteTimers = useRef([]);

  useEffect(() => () => deleteTimers.current.forEach(window.clearTimeout), []);

  useEffect(() => {
    if (highlightNoteId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightNoteId, notes]);

  const teamMembers = (team?.members || []).filter(m => m.email !== user?.email);

  const toggleRecipient = (email) => {
    setSelectedRecipients(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const openGmailWithNote = (noteText, recipients) => {
    const to = recipients.join(",");
    const subject = encodeURIComponent(`Minuta - ${clientName}`);
    const body = encodeURIComponent(`${noteText}\n\n---\nScritto da ${user?.name || "Team"} | ${new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}\nCliente: ${clientName}`);
    window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, "_blank");
  };

  const handleAdd = async () => {
    if (!newNote.trim()) return;
    setAdding(true);

    if (selectedRecipients.length > 0) {
      openGmailWithNote(newNote.trim(), selectedRecipients);
    }

    try {
      const res = await apiFetch(`/clients/${clientId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: newNote.trim(), recipient_emails: selectedRecipients })
      });
      if (res.ok) {
        toast.success(selectedRecipients.length > 0
          ? `Nota inviata a ${selectedRecipients.length} destinatario/i`
          : "Nota aggiunta (nessun destinatario notificato)");
        setNewNote("");
        setSelectedRecipients([]);
        onUpdate();
      }
    } catch (error) {
      toast.error("Errore");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!noteToDelete || deleteSubmitting) return;
    const noteId = noteToDelete.note_id;
    setDeleteSubmitting(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/notes/${noteId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Impossibile eliminare la nota");
      setDeletingNoteIds(prev => [...prev, noteId]);
      setNoteToDelete(null);
      toast.success("Nota eliminata");
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const timer = window.setTimeout(() => {
        onUpdate();
        setDeletingNoteIds(prev => prev.filter(id => id !== noteId));
        deleteTimers.current = deleteTimers.current.filter(id => id !== timer);
      }, reduceMotion ? 0 : 320);
      deleteTimers.current.push(timer);
    } catch (error) {
      toast.error("Impossibile eliminare la nota");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleResolveAlert = async (noteId) => {
    const res = await apiFetch(`/clients/${clientId}/notes/${noteId}/resolve`, { method: "PATCH" });
    if (res.ok) {
      toast.success("Alert segnato come risolto");
      onUpdate();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Errore");
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !replyToNote) return;
    setReplySending(true);
    try {
      const recipients = [];
      if (replyToNote.author_email && replyToNote.author_email !== user?.email) {
        recipients.push(replyToNote.author_email);
      }
      (replyToNote.recipients || []).forEach(r => {
        if (r.email && r.email !== user?.email && !recipients.includes(r.email)) {
          recipients.push(r.email);
        }
      });
      const res = await apiFetch(`/clients/${clientId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: replyText.trim(),
          recipient_emails: recipients,
          reply_to_note_id: replyToNote.note_id,
          is_alert: !!replyToNote.is_alert,
          alert_priority: replyToNote.alert_priority || "media",
          alert_source: "reply",
          alert_source_ref: replyToNote.note_id,
        }),
      });
      if (res.ok) {
        toast.success(`Risposta inviata a ${recipients.length} persona/e`);
        setReplyText("");
        setReplyToNote(null);
        onUpdate();
      } else {
        toast.error("Errore invio risposta");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setReplySending(false);
    }
  };

  const [filterText, setFilterText] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const filteredNotes = notes.filter(note => {
    if (filterText) {
      const s = filterText.toLowerCase();
      if (!note.text?.toLowerCase().includes(s) && !note.author_name?.toLowerCase().includes(s)) return false;
    }
    if (filterDateFrom) {
      if (new Date(note.created_at).toISOString().split("T")[0] < filterDateFrom) return false;
    }
    if (filterDateTo) {
      if (new Date(note.created_at).toISOString().split("T")[0] > filterDateTo) return false;
    }
    return true;
  });

  const [expandedNotes, setExpandedNotes] = useState({});

  // Auto-espandi la nota evidenziata da URL param
  useEffect(() => {
    if (highlightNoteId) {
      setExpandedNotes(prev => ({ ...prev, [highlightNoteId]: true }));
    }
  }, [highlightNoteId]);

  const toggleNoteExpanded = (noteId) => {
    setExpandedNotes(prev => ({ ...prev, [noteId]: !prev[noteId] }));
  };

  const expandAll = () => {
    const all = {};
    filteredNotes.forEach(n => { all[n.note_id] = true; });
    setExpandedNotes(all);
  };

  const collapseAll = () => {
    setExpandedNotes({});
  };

  const allExpanded = filteredNotes.length > 0 && filteredNotes.every(n => expandedNotes[n.note_id]);

  const hasFilters = filterText || filterDateFrom || filterDateTo;

  return (
    <SectionCard
      icon={MessageSquare}
      iconBg="#fff1e7"
      iconColor="#c2570f"
      title="Minute / Archivio comunicazioni"
      count={notes.length}
      testId="notes-section"
      action={
        notes.length > 0 ? (
          <button
            onClick={allExpanded ? collapseAll : expandAll}
            className="text-[12px] font-semibold text-[#5b5bd6] hover:text-[#4646be] hover:bg-[#eeeefc]/60 px-2.5 py-1 rounded-[8px] transition-colors inline-flex items-center gap-1"
          >
            {allExpanded ? (
              <>
                <ChevronUp className="w-3.5 h-3.5" />
                Comprimi tutte
              </>
            ) : (
              <>
                <ChevronDown className="w-3.5 h-3.5" />
                Espandi tutte
              </>
            )}
          </button>
        ) : null
      }
    >
      <div className={`rounded-[14px] p-[14px_16px_12px] border ${newNote.trim() ? "border-[#d7d7f2]" : "border-[#e9e9f1]"} bg-[#fbfbfd] transition-colors`}>
        <Textarea
          placeholder="Scrivi una nuova nota o minuta…"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          rows={3}
          className="border-0 bg-transparent shadow-none focus-visible:ring-0 px-0 text-[14.5px] leading-[1.6] font-medium min-h-[70px] resize-none"
          data-testid="new-note-input"
        />

        {teamMembers.length > 0 && newNote.trim() && (
          <div className="mt-3 p-3 rounded-[12px] border border-[#eeeef5] bg-white" data-testid="recipients-selector">
            <p className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#9095a1] mb-2">
              Destinatari minuta (riceveranno notifica + email):
            </p>
            <div className="flex flex-wrap gap-2">
              {teamMembers.map(member => (
                <button
                  key={member.email}
                  onClick={() => toggleRecipient(member.email)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                    selectedRecipients.includes(member.email)
                      ? "bg-[#eeeefc] text-[#5b5bd6] border-[#c9c9f0]"
                      : "bg-white text-muted-foreground border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`recipient-${member.email}`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs overflow-hidden shrink-0 ${
                    selectedRecipients.includes(member.email) ? "bg-[#5b5bd6] text-white" : "bg-slate-200 text-slate-500"
                  }`}>
                    {selectedRecipients.includes(member.email) ? (
                      "✓"
                    ) : member.picture ? (
                      <img src={member.picture} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      member.name.charAt(0)
                    )}
                  </span>
                  {member.name}
                  {member.role_label && (
                    <span className="text-xs opacity-60 ml-0.5">({member.role_label})</span>
                  )}
                </button>
              ))}
            </div>
            {selectedRecipients.length > 0 ? (
              <p className="text-xs text-[#5b5bd6] mt-2">
                ✓ {selectedRecipients.length} destinatario/i riceveranno notifica in-app e si aprirà Gmail per l&apos;email
              </p>
            ) : (
              <p className="text-xs text-amber-600 mt-2">
                ⚠️ Nessun destinatario selezionato: la nota verrà solo archiviata, nessuno riceverà notifica
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap justify-between items-center mt-2.5 gap-2">
          <button
            onClick={() => setAlertOpen(true)}
            className="h-[38px] px-3 rounded-[10px] border border-[#f0d6d2] bg-white text-[#c0392b] text-[12.5px] font-semibold hover:bg-[#fdecea] hover:border-[#f3c9c3] transition-colors inline-flex items-center gap-1.5"
            data-testid="open-alert-dialog-btn"
          >
            <AlertTriangle className="w-[15px] h-[15px]" />
            Segnala problema
          </button>
          <span className="text-[12.5px] font-medium text-[#9095a1]">
            {newNote.trim() ? "Apri la segnalazione con questo testo" : "Scrivi una nota per segnalare un problema"}
          </span>
          <button onClick={handleAdd} disabled={adding || !newNote.trim()} className={`${PRIMARY_BTN} h-[38px]`} data-testid="add-note-btn">
            <Plus className="w-4 h-4" />
            {selectedRecipients.length > 0 ? "Aggiungi nota e invia email" : "Aggiungi nota"}
          </button>
        </div>
      </div>

      <AlertRichiamoDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        clientId={clientId}
        clientName={clientName}
        prefillText={newNote.trim()}
        source="manual"
        onSent={onUpdate}
      />

      {notes.length > 0 && (
        <div className="flex items-center gap-3 mb-1 mt-4 flex-wrap" data-testid="notes-filters">
          <div className="relative flex-1 basis-full sm:basis-auto min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Cerca nelle note..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="pl-8 h-8 text-sm"
              data-testid="notes-filter-text"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-1 sm:flex-none min-w-[130px]">
            <span className="text-xs text-muted-foreground">Dal:</span>
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="h-8 text-sm w-full min-w-0 sm:w-36"
              data-testid="notes-filter-from"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-1 sm:flex-none min-w-[130px]">
            <span className="text-xs text-muted-foreground">Al:</span>
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="h-8 text-sm w-full min-w-0 sm:w-36"
              data-testid="notes-filter-to"
            />
          </div>
          {hasFilters && (
            <button className={GHOST_BTN} onClick={() => { setFilterText(""); setFilterDateFrom(""); setFilterDateTo(""); }}>
              Pulisci filtri
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col mt-4 max-h-[580px] overflow-y-auto pr-1 py-1">
        {filteredNotes.length > 0 ? (
          filteredNotes.map(note => {
            const isHighlight = note.note_id === highlightNoteId;
            const isAlert = !!note.is_alert;
            const isResolved = note.alert_status === "resolved";
            const isExpanded = !!expandedNotes[note.note_id];
            const isDeleting = deletingNoteIds.includes(note.note_id);
            const priority = (note.alert_priority || "media");
            const priorityStyle = {
              bassa: { border: "#a7e3c4", bg: "#f1faf5", chip: "bg-emerald-100 text-emerald-800" },
              media: { border: "#f0cf8e", bg: "#fffaf0", chip: "bg-amber-100 text-amber-800" },
              alta: { border: "#f0a79c", bg: "#fdf3f1", chip: "bg-red-100 text-red-800" },
            }[priority] || { border: "#f0cf8e", bg: "#fffaf0", chip: "bg-amber-100 text-amber-800" };

            // Anteprima sintetica del testo per la modalità collassata (prima riga senza a capo)
            const firstLine = (note.text || "").replace(/[\r\n]+/g, " ").trim();

            return (
              <div
                key={note.note_id}
                className={`grid transition-[grid-template-rows,opacity,transform,padding] duration-300 ease-in-out motion-reduce:transition-none ${isDeleting ? "grid-rows-[0fr] opacity-0 -translate-x-3 pb-0" : "grid-rows-[1fr] opacity-100 translate-x-0 pb-4"}`}
              >
              <div className="min-h-0 overflow-hidden">
              <div
                ref={isHighlight ? highlightRef : null}
                className={`rounded-[16px] border bg-white transition-all duration-200 shadow-[0_4px_16px_rgba(15,23,42,0.06),0_1px_3px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.09)] ${
                  isHighlight
                    ? "border-indigo-400 bg-indigo-50/70 ring-2 ring-indigo-200"
                    : isAlert
                    ? isResolved
                      ? "border-[#e6e6f0] bg-[#fafafc] opacity-85"
                      : "border-l-[6px] border-[#f0dedb]"
                    : "border-[#e9e9f2]"
                }`}
                style={
                  !isHighlight && isAlert && !isResolved
                    ? { borderLeftColor: priorityStyle.border }
                    : {}
                }
                data-testid={`note-${note.note_id}`}
              >
                {/* Header cliccabile per aprire/chiudere la nota */}
                <div
                  onClick={() => toggleNoteExpanded(note.note_id)}
                  className="flex items-center justify-between p-3.5 cursor-pointer select-none hover:bg-slate-50/80 rounded-[14px] transition-colors"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  aria-controls={`note-content-${note.note_id}`}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleNoteExpanded(note.note_id); } }}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1 pr-2 flex-wrap">
                    {/* Icona expand/collapse */}
                    <div className="w-5 h-5 rounded-md flex items-center justify-center text-[#9095a1] hover:text-[#15161c] shrink-0">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>

                    {/* Avatar autore */}
                    <div
                      className="w-[28px] h-[28px] rounded-[8px] flex items-center justify-center shrink-0 overflow-hidden"
                      style={isAlert && !isResolved ? { background: "#fdecea" } : { background: "#eeeefc" }}
                    >
                      {note.author_picture ? (
                        <img src={note.author_picture} alt={note.author_name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[12px] font-bold" style={isAlert && !isResolved ? { color: "#c0392b" } : { color: "#5b5bd6" }}>
                          {note.author_name?.charAt(0) || "?"}
                        </span>
                      )}
                    </div>

                    <span className="text-[13.5px] font-semibold text-[#15161c] truncate">{note.author_name}</span>

                    {isAlert && (
                      <Badge className={`${priorityStyle.chip} border-0 uppercase text-[9.5px] font-bold py-0.5 px-2`} data-testid={`alert-badge-${note.note_id}`}>
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Alert · {priority}
                      </Badge>
                    )}

                    {isAlert && isResolved && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px] py-0.5 px-2" data-testid={`alert-resolved-${note.note_id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" />Risolto
                      </Badge>
                    )}

                    {note.reply_to_note_id && (
                      <Badge variant="outline" className="text-[10px] text-slate-600 py-0.5 px-2 border-slate-200">
                        <Reply className="w-3 h-3 mr-1" />Risposta
                      </Badge>
                    )}

                    <span className="text-[12px] scheda-font-mono text-[#9095a1]">
                      {new Date(note.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>

                    {/* Quando collassata, mostra preview troncata a una riga */}
                    {!isExpanded && (
                      <span className="w-full pl-[34px] text-[13px] text-[#6b7280] truncate block font-normal">
                        {firstLine}
                      </span>
                    )}
                  </div>

                  {/* Azione elimina */}
                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {(note.author_id === user?.user_id || user?.email === "info@diecixcento.com" || user?.role === "pm" || user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale") && (
                      <button
                        className="h-7 w-7 rounded-[8px] flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        onClick={() => setNoteToDelete(note)}
                        data-testid={`delete-note-${note.note_id}`}
                        title="Elimina nota"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Contenuto espanso */}
                <div
                  id={`note-content-${note.note_id}`}
                  aria-hidden={!isExpanded}
                  inert={!isExpanded}
                  className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none ${isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="min-h-0 overflow-hidden">
                  <div className="px-4 pb-4 pt-1 border-t border-[#f2f2f7]/80">
                    {note.alert_source_label && (
                      <p className="text-[11.5px] text-slate-500 italic mt-2">Origine: {note.alert_source_label}</p>
                    )}
                    {note.recipients && note.recipients.length > 0 && (
                      <p className="text-[11.5px] text-slate-500 mt-1">
                        → {note.recipients.map(r => r.name).join(", ")}
                      </p>
                    )}

                    <p className="text-[14px] leading-[1.65] text-[#2e3440] whitespace-pre-wrap mt-2.5">
                      {note.text}
                    </p>

                    {isAlert && (
                      <div className="mt-4 flex flex-wrap gap-2 pt-2 border-t border-[#f4f4f8]">
                        <button
                          onClick={() => { setReplyToNote(note); setReplyText(""); }}
                          className={SECONDARY_BTN}
                          style={{ height: 30 }}
                          data-testid={`reply-alert-${note.note_id}`}
                        >
                          <Reply className="w-3.5 h-3.5" />
                          Rispondi
                        </button>
                        {!isResolved && (
                          <button
                            onClick={() => handleResolveAlert(note.note_id)}
                            className="h-[30px] px-3 rounded-[10px] border border-emerald-300 bg-emerald-50/50 text-emerald-700 text-[12.5px] font-semibold hover:bg-emerald-100/70 transition-colors inline-flex items-center gap-1"
                            data-testid={`resolve-alert-${note.note_id}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Segna risolto
                          </button>
                        )}
                      </div>
                    )}

                    {replyToNote?.note_id === note.note_id && (
                      <div className="mt-3 border-t border-[#eeeef5] pt-3" data-testid={`reply-form-${note.note_id}`}>
                        <Textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder={`Rispondi a ${note.author_name}…`}
                          rows={3}
                          className="text-sm rounded-[10px] border-[#e4e4ed]"
                          data-testid={`reply-text-${note.note_id}`}
                        />
                        <div className="flex gap-2 mt-2 justify-end">
                          <button
                            className={GHOST_BTN}
                            onClick={() => { setReplyToNote(null); setReplyText(""); }}
                            disabled={replySending}
                          >Annulla</button>
                          <button
                            onClick={handleReply}
                            disabled={replySending || !replyText.trim()}
                            className={PRIMARY_BTN}
                            data-testid={`reply-send-${note.note_id}`}
                          >
                            <Reply className="w-3 h-3" />
                            {replySending ? "Invio…" : "Invia risposta"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              </div>
              </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-[28px] h-[28px] mx-auto mb-2 text-[#c3c6d0]" strokeWidth={1.7} />
            <p className="text-[13.5px] font-medium text-[#9095a1]">{hasFilters ? "Nessuna nota trovata con questi filtri" : "Nessuna nota ancora. Aggiungi la prima."}</p>
          </div>
        )}
      </div>
      <AlertDialog open={!!noteToDelete} onOpenChange={(open) => { if (!open && !deleteSubmitting) setNoteToDelete(null); }}>
        <AlertDialogContent className="scheda-font-ui max-w-md rounded-[18px] border-[#e9e9f1] duration-300 motion-reduce:animate-none" data-testid="delete-note-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#15161c]">Eliminare questa nota?</AlertDialogTitle>
            <AlertDialogDescription>
              La nota verrà eliminata definitivamente. Questa operazione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <p className="rounded-[10px] bg-[#f7f7fa] px-3 py-2 text-[13px] text-[#565d6d] line-clamp-2">{noteToDelete?.text}</p>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>Annulla</AlertDialogCancel>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteSubmitting}
              className="inline-flex h-9 items-center justify-center rounded-[10px] bg-red-600 px-4 text-[13px] font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-50"
              data-testid="confirm-delete-note"
            >
              {deleteSubmitting ? "Eliminazione…" : "Elimina nota"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}
